<template>
    <div class="supplement-review">
        <!-- 헤더 — 이력이 없어도 어느 업체 화면인지 보여야 한다(이 화면에서 비용이 드는 요청을 한다) → 설명 자리.
             목록에서 왔으면 뒤로가기로 필터·페이지를 되살리므로 나가는 길은 주소가 아니라 동작(goBack) -->
        <admin-page-header title="AI 보완검토" :description="headerLabel || ''" :back="{label: '목록으로', onClick: goBack}">
            <template v-if="job" #badge>
                <b-badge :variant="jobStatusVariant(job.status)">{{ jobStatusLabel(job.status) }}</b-badge>
            </template>
        </admin-page-header>

        <!-- 진행률 -->
        <div v-if="job && isJobActive" class="mb-3">
            <b-progress :max="job.items_total || 1" height="1.4rem" animated>
                <b-progress-bar :value="job.items_done"
                                :label="`${job.items_done}/${job.items_total} 문항 판정`"/>
            </b-progress>
            <div v-if="!pollError" class="text-muted small mt-1">초벌 판정이 진행 중입니다. 자동으로 갱신됩니다.</div>
        </div>
        <!-- 재조회 실패 — 진행 중 폴링과 저장·재판정 뒤 재조회가 함께 쓴다 -->
        <div v-if="job && pollError" class="text-warning small mb-3">
            {{ isJobActive ? '진행 상황을 확인하지 못했습니다. 표시된 진행률은 마지막으로 확인한 값입니다.'
                : '최신 결과를 불러오지 못했습니다. 화면의 내용은 마지막으로 불러온 값입니다.' }}
            <b-button size="sm" variant="outline-secondary" class="ml-2" @click="refreshJob">지금 새로고침</b-button>
        </div>
        <b-alert :show="!!(job && job.error_code)" variant="warning" class="py-2">
            {{ job && jobErrorLabel(job.error_code) }}
        </b-alert>

        <!-- 요약 -->
        <div v-if="summary" class="d-flex mb-3" style="gap: .5rem">
            <b-badge variant="success" class="p-2">보완 불필요 {{ summary.no_supplement }}</b-badge>
            <b-badge variant="danger" class="p-2">보완 필요 {{ summary.supplement_required }}</b-badge>
            <b-badge variant="warning" class="p-2">평가자 확인 {{ summary.review_required }}</b-badge>
            <b-badge variant="secondary" class="p-2">처리 실패 {{ summary.processing_failed }}</b-badge>
        </div>
        <b-alert :show="crossIssues.length > 0" variant="info" class="py-2">
            <div class="font-weight-bold mb-1">문항 간 정합성 검토 필요 ({{ crossIssues.length }})</div>
            <div v-for="issue in crossIssues" :key="issue.issue_id" class="small">
                · [{{ (issue.related_sub_codes || []).join(', ') }}] {{ issue.description }}
            </div>
        </b-alert>

        <b-tabs v-if="job" v-model="activeTab" content-class="pt-3" no-fade>
            <!-- 문항 판정 탭 -->
            <b-tab title="문항 판정">
                <div class="ml-auto">
                    <b-button size="sm" variant="primary"
                              :disabled="creating || isJobActive"
                              @click="createJob">
                        <b-spinner v-if="creating" small class="mr-1"/>
                        새 보완검토 요청
                    </b-button>
                </div>
                <!-- AI 판정 / 현재 상태 필터 -->
                <div v-if="items.length"
                     class="d-flex align-items-center justify-content-end flex-wrap mb-2"
                     style="gap: .5rem">
                    <!-- 실패 문항 전체를 한 번에 재판정(백엔드 bulk-rerun). 단건 rerun 을
                         반복하면 잡이 RUNNING 이 돼 나머지 버튼이 사라지므로, 완료 잡에
                         실패가 있으면 이 버튼으로 한 번에 병렬 재판정한다. -->
                    <b-button v-if="isJobCompleted && failedItems.length" size="sm"
                              variant="outline-danger" class="mr-auto"
                              :disabled="reruningAll" @click="rerunAllFailed">
                        <b-spinner v-if="reruningAll" small class="mr-1"/>
                        실패 {{ failedItems.length }}건 모두 재판정
                    </b-button>
                    <span v-if="visibleItems.length !== items.length" class="text-muted small">
                        {{ visibleItems.length }}/{{ items.length }} 문항 표시
                    </span>
                    <!-- 보기: 문항별(문항 그룹 묶음 · 정렬은 묶음 안에서) / 전체 목록(묶음 없이 문항 칸 · 정렬은 표 전체) -->
                    <b-form-radio-group v-model="itemView" buttons size="sm" button-variant="outline-secondary"
                                        aria-label="보기"
                                        :options="[{text: '문항별', value: 'group'}, {text: '전체 목록', value: 'list'}]"/>
                    <b-form-select v-model="itemFilter.original" size="sm" style="width: 170px"
                                   :options="itemFilterOptions('AI 판정')"/>
                    <b-form-select v-model="itemFilter.current" size="sm" style="width: 170px"
                                   :options="itemFilterOptions('현재 상태')"/>
                </div>
                <!-- DS 표 규칙: 행 44 고정 · 칸마다 한 줄(넘치면 줄이고 전체는 title). 폭은 머리가 정하고 문항 · 질문이 남는 폭을 갖는다.
                     문항별 보기는 문항(대분류 · 문항 그룹)을 칸 대신 연속된 같은 문항 위 묶음 머리 줄에 한 번만 쓴다 -->
                <b-table-simple hover responsive
                                :table-class="['ds-table', 'ds-table-fixed', 'sr-item-table', {'sr-item-table-list': itemView === 'list'}]">
                    <b-thead>
                        <b-tr>
                            <b-th style="width: 96px">문항 번호</b-th>
                            <b-th v-if="itemView === 'list'">문항</b-th>
                            <b-th>질문</b-th>
                            <b-th style="width: 128px">판정 근거</b-th>
                            <!-- 정렬 헤더 — 키보드로 누를 수 있게 버튼, 정렬 상태는 aria-sort.
                                 aria-sort 가 켜는 BootstrapVue 배경 화살표는 끄고 글자 옆 ↕ 하나만 보인다.
                                 aria-sort 머리는 DS 좌우 16 이 빠지므로 여기서 준다 -->
                            <b-th v-for="col in [{key: 'original', label: 'AI 판정', width: '168px'}, {key: 'current', label: '현재 상태', width: '128px'}]"
                                  :key="col.key" style="background-image:none; padding-left:16px; padding-right:16px"
                                  :style="{width: col.width}"
                                  class="user-select-none"
                                  :aria-sort="itemAriaSort(col.key)">
                                <button type="button" title="클릭하여 정렬"
                                        class="btn btn-link p-0 text-reset font-weight-bold text-decoration-none"
                                        @click="toggleItemSort(col.key)">
                                    {{ col.label }} <AppIcon :name="itemSortIcon(col.key)" class="text-muted"/>
                                </button>
                            </b-th>
                            <!-- 아이콘 칸은 좁아서 머리 글자가 넘치지 않게 좌우 8 -->
                            <b-th class="text-center" style="width: 72px; padding-left: 8px; padding-right: 8px">메일 포함</b-th>
                            <b-th class="text-center" style="width: 64px; padding-left: 8px; padding-right: 8px">확인</b-th>
                        </b-tr>
                    </b-thead>
                    <!-- 묶음마다 tbody 하나 — 머리 줄(th scope=rowgroup)이 아래 문항 행들의 제목이 된다. 전체 목록은 머리 줄 없는 묶음 하나 -->
                    <b-tbody v-for="group in visibleGroups" :key="group.key">
                        <b-tr v-if="group.label" class="sr-group-row">
                            <b-th :colspan="itemColspan" scope="rowgroup">
                                <span class="ds-ellipsis" :title="group.label.title">
                                    <span v-if="group.label.major" class="ds-sub mr-2">{{ group.label.major }}</span>{{ group.label.name }}<span class="ds-sub ml-2">{{ group.items.length }}문항</span>
                                </span>
                            </b-th>
                        </b-tr>
                        <!-- 키보드 진입점은 질문 칸 버튼 — 행은 마우스 클릭만 받는다 -->
                        <b-tr v-for="item in group.items" :key="item.item_id"
                              style="cursor:pointer"
                              @click="openDetail(item)">
                            <b-td class="ds-cell-nowrap">{{ item.sub_code }}</b-td>
                            <b-td v-if="itemView === 'list'">
                                <span class="ds-ellipsis" :title="questionGroupLabel(item).title">
                                    {{ questionGroupLabel(item).name }} <span v-if="questionGroupLabel(item).major" class="ds-sub">{{ questionGroupLabel(item).major }}</span>
                                </span>
                            </b-td>
                            <b-td>
                                <div class="d-flex align-items-center">
                                    <b-spinner v-if="openingItemId === item.item_id" small class="mr-1 flex-shrink-0"
                                               label="문항 상세를 불러오는 중"/>
                                    <button type="button" class="ds-detail-link ds-ellipsis"
                                            :title="item.question_text || null"
                                            @click.stop="openDetail(item)">{{ item.question_text || '-' }}</button>
                                </div>
                            </b-td>
                            <b-td>
                                <!-- 판정 근거 — 구조 규칙(답변 없음 · 첨부 없음 …) vs AI 증빙 분석, 사유 전문은 title -->
                                <span v-if="verdictBasis(item)" class="ds-tag"
                                      :title="verdictBasis(item).reason">{{ verdictBasis(item).text }}</span>
                                <span v-else class="ds-sub">-</span>
                            </b-td>
                            <b-td class="ds-cell-nowrap">
                                <span class="ds-status" :class="`ds-status-${statusVariant(item.original_status)}`">{{ statusLabel(item.original_status) }}</span> <span v-if="item.processing_status === 'PENDING' || item.processing_status === 'PROCESSING'" class="ds-sub">판정 중…</span> <span v-if="item.processing_status === 'FAILED'" class="ds-status ds-status-danger" :title="jobErrorLabel(item.error_code)">실패</span>
                                <!-- 처리 실패 문항만: 완료(DONE/PARTIAL) 잡에서 단일 문항 재판정
                                     (백엔드 POST /items/{id}/rerun — 활성 잡엔 409 이라 완료일 때만).
                                     막힌 아이콘 버튼은 클릭을 통과시키므로(pointer-events) 감싸개가 받아 행 클릭(상세 열기)으로 새지 않게 한다 -->
                                <span v-if="item.processing_status === 'FAILED' && isJobCompleted"
                                      class="d-inline-flex align-middle ml-1" @click.stop>
                                    <button type="button" class="ds-row-icon-btn"
                                            :disabled="isReruning(item)"
                                            :aria-label="`${item.sub_code} 재판정`" :title="`${item.sub_code} 재판정`"
                                            @click.stop="rerunItem(item)">
                                        <b-spinner v-if="isReruning(item)" small/>
                                        <AppIcon v-else name="refresh"/>
                                    </button>
                                </span>
                            </b-td>
                            <b-td class="ds-cell-nowrap">
                                <span class="ds-status" :class="`ds-status-${statusVariant(item.current_status)}`">{{ statusLabel(item.current_status) }}</span>
                                <!-- svg 는 title 속성 툴팁이 없어 감싼 span 에 둔다 -->
                                <span v-if="item.has_override" class="ml-1 text-muted" title="평가자 수정 있음">
                                    <AppIcon name="user-cog"/>
                                    <span class="sr-only">평가자 수정 있음</span>
                                </span>
                            </b-td>
                            <b-td class="text-center">
                                <template v-if="includedInEmail(item)">
                                    <fa icon="check" class="text-danger"/>
                                    <span class="sr-only">메일 포함</span>
                                </template>
                            </b-td>
                            <!-- 평가자 '확인함' 표시 — 판정과 무관한 메타, 토글은 상세 모달에서만 -->
                            <b-td class="text-center">
                                <template v-if="item.reviewer_checked">
                                    <b-icon icon="check-circle-fill"
                                            variant="success" :title="reviewerCheckedLabel(item)"/>
                                    <span class="sr-only">확인함</span>
                                </template>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                    <b-tbody v-if="!visibleItems.length">
                        <b-tr v-if="!items.length">
                            <b-td :colspan="itemColspan" class="text-center text-muted py-4">
                                {{ emptyItemsMessage }}
                            </b-td>
                        </b-tr>
                        <b-tr v-else>
                            <b-td :colspan="itemColspan" class="text-center text-muted py-4">
                                필터 조건에 맞는 문항이 없습니다.
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-tab>

            <!-- 잡 단위 "AI 대화" 탭은 제거됨 — 대화는 문항 상세 모달의
                 우측 문항별 패널로 일원화 (백엔드 잡 단위 스레드 API 는 유지) -->

            <!-- 이메일 초안 탭 -->
            <b-tab title="보완요청 메일 초안" :disabled="!isJobCompleted">
                <!-- 조회 실패를 '초안 없음'으로 보이면 이미 만든 초안을 모르고 다시 만든다 — 생성 버튼 대신 재시도 -->
                <div v-if="!draft && draftLoadError" class="text-center py-4">
                    <div class="text-danger mb-2">메일 초안을 불러오지 못했습니다.</div>
                    <b-button size="sm" variant="outline-secondary" @click="loadDraftSilently">다시 시도</b-button>
                </div>
                <div v-else-if="!draft" class="text-center py-4">
                    <b-button variant="primary" :disabled="draftLoading" @click="createDraft(false)">
                        <b-spinner v-if="draftLoading" small class="mr-1"/>
                        확정된 보완 문항으로 초안 생성
                    </b-button>
                    <div class="text-muted small mt-2">
                        현재 상태가 "보완 필요"(또는 메일 포함으로 확정)인 문항만 표에 들어갑니다.
                    </div>
                </div>
                <template v-else>
                    <!-- 메일 표는 초안 생성 때 본문에 굳는다 — 이후 문항 판정이 바뀌면 다시 만들어야 반영된다 -->
                    <b-alert :show="draftOutdated" variant="warning"
                             class="py-2 d-flex align-items-center flex-wrap" style="gap:.5rem">
                        <span>
                            초안을 만든 뒤 문항 판정이 바뀌었습니다. 메일 표에는 반영되지 않았습니다.
                            반영하려면 [초안 다시 만들기]를 누르세요(직접 고친 제목·본문·수신자는 초기화됩니다).
                        </span>
                        <b-button size="sm" variant="outline-warning" :disabled="draftLoading"
                                  @click="confirmRegenerate">초안 다시 만들기</b-button>
                    </b-alert>
                    <!-- 행1: 발신자 -->
                    <b-form-group label="발신자" label-size="sm" label-cols-sm="2">
                        <b-form-input value="info@qesg.co.kr" disabled readonly size="sm"/>
                    </b-form-group>
                    <!-- 행2: 수신자 — 기본 접힘(첫 1명 + 외 N명), 펼쳐야 제거/추가 가능.
                         0명이면 접힘/펼침과 무관하게 안내 + 추가 입력을 바로 노출 -->
                    <b-form-group label="수신자" label-size="sm" label-cols-sm="2">
                        <div v-if="!(draft.recipients || []).length" class="text-muted small mb-1">
                            수신자가 없습니다. 아래에서 추가하세요.
                        </div>
                        <div v-else-if="!recipientsExpanded" class="d-flex align-items-center small">
                            <b-badge :variant="recipientRoleVariant(draft.recipients[0])" class="mr-2">
                                {{ recipientRoleLabel(draft.recipients[0]) }}
                            </b-badge>
                            <span class="text-truncate" :title="draft.recipients[0].email">
                                <template v-if="draft.recipients[0].name">{{ draft.recipients[0].name }} &lt;{{ draft.recipients[0].email }}&gt;</template>
                                <template v-else>{{ draft.recipients[0].email }}</template>
                            </span>
                            <span v-if="draft.recipients.length > 1" class="text-muted ml-2 flex-shrink-0">
                                외 {{ draft.recipients.length - 1 }}명
                            </span>
                            <b-button size="sm" variant="outline-secondary" class="sr-panel-toggle ml-2"
                                      title="수신자 목록 펼치기" aria-label="수신자 목록 펼치기"
                                      @click="recipientsExpanded = true">
                                <b-icon icon="chevron-down"/>
                            </b-button>
                        </div>
                        <template v-else>
                            <div class="d-flex align-items-center text-muted small mb-1">
                                <span>기본값은 이 업체 담당자 전원입니다. 제거하거나 임의 주소를 추가할 수 있습니다.</span>
                                <b-button size="sm" variant="outline-secondary" class="sr-panel-toggle ml-auto"
                                          title="수신자 목록 접기" aria-label="수신자 목록 접기"
                                          @click="recipientsExpanded = false">
                                    <b-icon icon="chevron-up"/>
                                </b-button>
                            </div>
                            <div class="border rounded p-2 mb-1"
                                 style="max-height: 200px; overflow-y:auto">
                                <div v-for="(rcpt, idx) in (draft.recipients || [])" :key="idx"
                                     class="d-flex align-items-center small mb-1">
                                    <b-badge :variant="recipientRoleVariant(rcpt)" class="mr-2">
                                        {{ recipientRoleLabel(rcpt) }}
                                    </b-badge>
                                    <span class="text-truncate" :title="rcpt.email">
                                        <template v-if="rcpt.name">{{ rcpt.name }} &lt;{{ rcpt.email }}&gt;</template>
                                        <template v-else>{{ rcpt.email }}</template>
                                    </span>
                                    <b-button size="sm" variant="link" class="ml-auto text-danger p-0"
                                              @click="removeRecipient(idx)">제거</b-button>
                                </div>
                            </div>
                        </template>
                        <b-input-group v-if="!(draft.recipients || []).length || recipientsExpanded" size="sm">
                            <b-form-input v-model="newRecipientEmail"
                                          placeholder="임의 수신 이메일 추가 (도달 검증 안 함)"
                                          :state="recipientError ? false : null"
                                          @input="recipientError = ''"
                                          @keyup.native.enter="addRecipient"/>
                            <b-input-group-append>
                                <b-button variant="outline-secondary" @click="addRecipient">추가</b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <div v-if="recipientError" class="text-danger small mt-1">{{ recipientError }}</div>
                    </b-form-group>
                    <!-- 행2.5: 국문/영문 전환 — 영문이 있을 때만 노출. 제목·본문만 로케일별,
                         기한/URL/수신자/표는 공유. 전환 전 현재 본문을 플러시(switchLocale)하고
                         에디터는 :key 로 재마운트 -->
                    <b-form-group v-if="hasEn" label="언어" label-size="sm" label-cols-sm="2">
                        <div class="d-flex align-items-center flex-wrap" style="gap:.5rem">
                            <b-form-radio-group :checked="activeLocale" buttons size="sm"
                                                button-variant="outline-secondary"
                                                :options="[{text: '국문', value: 'ko'}, {text: '영문', value: 'en'}]"
                                                @input="switchLocale"/>
                            <!-- §11: 영문 차단(캠페인 영문명/템플릿 영문 미등록) →
                                 편집은 되지만 영문 발송과 재번역은 막고 사유별 바로가기를 준다 -->
                            <b-badge v-if="draft.en_send_blocked" variant="warning">
                                영문 발송 불가 — {{ enBlockedMessage }}
                            </b-badge>
                            <b-button size="sm" variant="outline-primary"
                                      :disabled="draftLoading || translating || !!draft.en_send_blocked"
                                      :title="draft.en_send_blocked ? enBlockedMessage
                                          : '영문 본문을 템플릿으로 다시 만들고 피드백만 새로 번역합니다'"
                                      @click="translateDraft">
                                <b-spinner v-if="translating" small class="mr-1"/>
                                영문 다시 번역하기
                            </b-button>
                            <b-button v-if="draft.en_send_blocked && enBlockedTemplateUrl"
                                      size="sm" variant="outline-primary" class="flex-shrink-0"
                                      target="_blank" rel="noopener" :href="enBlockedTemplateUrl">
                                템플릿 관리로 이동
                            </b-button>
                            <!-- 영문 표에 '번역 없음' 대체 항목이 있으면 경고 배지 + 질문지 번역 바로가기.
                                 현재 편집본 기준 라이브 카운트(저장본 en_unresolved_placeholders 는 참고용) -->
                            <template v-if="enUnresolvedCount > 0">
                                <b-badge variant="warning">번역 없음 {{ enUnresolvedCount }}개</b-badge>
                                <b-link v-if="surveyTranslationUrl" class="small flex-shrink-0"
                                        target="_blank" rel="noopener" :href="surveyTranslationUrl">
                                    질문지 번역 바로가기
                                </b-link>
                            </template>
                            <b-button v-if="activeLocale === 'en'" size="sm" variant="link"
                                      class="text-danger p-0" :disabled="draftLoading"
                                      @click="confirmDeleteEn">영문 삭제</b-button>
                        </div>
                    </b-form-group>
                    <!-- 행3: 제목 — 영문이 없으면 이 행 우측에 번역 버튼 하나만 둔다 -->
                    <b-form-group label="제목" label-size="sm" label-cols-sm="2">
                        <div class="d-flex align-items-center flex-wrap" style="gap:.5rem">
                            <b-form-input v-model="draft[subjectField]" size="sm" class="flex-grow-1"
                                          style="min-width:14rem"
                                          :placeholder="activeLocale === 'en' ? '영문 제목 (비우면 국문 제목으로 발송)' : ''"/>
                            <template v-if="!hasEn">
                                <b-button size="sm" variant="outline-primary" class="flex-shrink-0"
                                          :disabled="draftLoading || translating || !!draft.en_send_blocked"
                                          :title="draft.en_send_blocked ? '' : '국문 초안을 영문으로 번역합니다 (미저장 편집은 먼저 저장)'"
                                          @click="translateDraft">
                                    <b-spinner v-if="translating" small class="mr-1"/>
                                    영문 번역하기
                                </b-button>
                                <template v-if="draft.en_send_blocked">
                                    <span class="small text-warning">{{ enBlockedMessage }}</span>
                                    <b-button v-if="enBlockedTemplateUrl"
                                              size="sm" variant="outline-primary" class="flex-shrink-0"
                                              target="_blank" rel="noopener" :href="enBlockedTemplateUrl">
                                        템플릿 관리로 이동
                                    </b-button>
                                </template>
                            </template>
                        </div>
                    </b-form-group>
                    <div class="d-flex mb-2">
                        <b-button size="sm" variant="outline-warning" :disabled="draftLoading"
                                  @click="confirmRegenerate">초안 다시 만들기</b-button>
                    </div>
                    <!-- 행4: 변수 패널(접기 가능) | 본문 에디터 -->
                    <b-row>
                    <b-col v-show="!panelCollapsed" :md="panelCollapsed ? 0 : 5">
                        <div class="d-flex align-items-center mb-3">
                            <b-button v-b-toggle.variable-guide size="sm" variant="link"
                                      class="p-0 text-muted">
                                변수 사용법 안내
                            </b-button>
                            <b-button size="sm" variant="outline-secondary" class="sr-panel-toggle ml-auto"
                                      title="변수 패널 접기" aria-label="변수 패널 접기"
                                      @click="panelCollapsed = true">
                                <b-icon icon="chevron-double-left"/>
                            </b-button>
                        </div>
                        <b-collapse id="variable-guide" class="mb-3">
                            <div class="border rounded p-2 small bg-light">
                                <p class="mb-1">HTML 본문에서 아래 변수를 사용하면 발송 시 실제 값으로 치환됩니다.</p>
                                <table class="table table-sm table-borderless mb-0">
                                    <tr>
                                        <td><code v-pre>{{ campaign_name }}</code></td>
                                        <td>캠페인(평가) 이름</td>
                                    </tr>
                                    <tr>
                                        <td><code v-pre>{{ company_name }}</code></td>
                                        <td>피평가 업체명</td>
                                    </tr>
                                    <tr>
                                        <td><code v-pre>{{ supplement_deadline }}</code></td>
                                        <td>보완 기한 (날짜, 시간은 선택)</td>
                                    </tr>
                                    <tr>
                                        <td><code v-pre>{{ system_url }}</code></td>
                                        <td>평가 시스템 접속 URL</td>
                                    </tr>
                                </table>
                                <p v-pre class="mb-0 text-muted">예: 이번 {{ campaign_name }}에 참여해주셔서 감사합니다.</p>
                                <p class="mb-0 text-muted">본문의 변수 값은 [저장 + 미리보기] 이후에 반영됩니다.</p>
                                <p class="mb-0 text-muted">영문 본문에서는 캠페인/업체 영문명으로 치환됩니다 (영문명 미등록 시 국문).</p>
                            </div>
                        </b-collapse>
                        <b-form-group label-size="sm">
                            <template #label>
                                보완 기한
                                <code v-pre class="ml-1 small text-info">{{ supplement_deadline }}</code>
                            </template>
                            <div class="text-muted small mb-1">
                                비워두면 자동 기한(발송일 + 2영업일). 시간을 비우면 날짜만 적용됩니다.
                                HTML 본문에서 <code v-pre>{{ supplement_deadline }}</code>으로 참조됩니다.
                                <span v-if="draft.deadline_computed">
                                    현재 자동 기한: <strong>{{ draft.deadline_computed }}</strong>
                                    <span class="text-muted">(오늘 기준 · 발송 시 발송일 기준으로 재계산)</span>
                                </span>
                            </div>
                            <b-input-group size="sm">
                                <b-form-datepicker v-model="deadlineDate" locale="ko-KR"
                                                   placeholder="날짜 선택" :min="todayYmd"
                                                   reset-button label-reset-button="자동 기한으로"/>
                                <b-form-input v-model="deadlineTime" type="time"
                                              aria-label="보완 기한 시간 (선택)"/>
                            </b-input-group>
                            <div v-if="deadlineTime && !deadlineDate" class="text-warning small mt-1">
                                날짜를 선택해야 시간이 적용됩니다.
                            </div>
                            <div v-if="draft.deadline_holidays_applied === false"
                                 class="text-warning small mt-1">
                                ※ 공휴일은 반영되지 않습니다(주말만 제외). 공휴일이 낀 기간이면 기한을 직접 입력해 주세요.
                            </div>
                        </b-form-group>
                        <b-form-group label-size="sm">
                            <template #label>
                                평가 시스템 URL
                                <code v-pre class="ml-1 small text-info">{{ system_url }}</code>
                            </template>
                            <div class="text-muted small mb-1">
                                업체가 접속할 URL. HTML 본문에서
                                <code v-pre>{{ system_url }}</code>로 참조됩니다.
                            </div>
                            <b-form-input v-model="draft.system_url" size="sm"
                                          placeholder="예: https://saq.qesg.co.kr"/>
                        </b-form-group>
                    </b-col>
                    <b-col :md="panelCollapsed ? 12 : 7">
                        <div class="d-flex align-items-center flex-wrap mb-2"
                             style="gap:.25rem">
                            <b-button v-if="panelCollapsed" size="sm" variant="outline-secondary"
                                      class="sr-panel-toggle mr-2"
                                      title="변수 패널 열기" aria-label="변수 패널 열기"
                                      @click="panelCollapsed = false">
                                <b-icon icon="chevron-double-right"/>
                            </b-button>
                            <span class="small text-muted mr-1">변수 삽입:</span>
                            <b-button v-for="v in supplementVariables" :key="v.key"
                                      size="sm" variant="outline-info" class="py-0"
                                      @click="insertSupplementVariable(v.key)">
                                {{ v.label }}
                            </b-button>
                        </div>
                        <!-- :key=activeLocale — 로케일 전환 시 재마운트해 캔버스 상태/디바운스 잔재를 격리 -->
                        <HtmlSplitEditor ref="bodyEditor" :key="activeLocale" v-model="draft[bodyField]"
                                         visual default-mode="visual"
                                         :values="appliedVariableValues[activeLocale]"
                                         :preview-value="previewBodyHtml"
                                         :refresh-key="previewRefreshKey"
                                         placeholder="메일 본문 HTML을 입력하세요"
                                         storage-key="supplement-review:html-split-ratio"
                                         @unsupported="onBodyUnsupported"/>
                    </b-col>
                    </b-row>
                    <!-- 저장/발송 — 패널 접힘과 무관하게 항상 보이도록 행3 아래 -->
                    <div class="d-flex mt-2" style="gap:.5rem">
                        <b-button size="sm" variant="primary" :disabled="draftLoading"
                                  @click="saveDraft">저장 + 미리보기</b-button>
                        <!-- 발송 테스트 — 저장된 초안(현재 로케일)을 현재 로그인 사용자 1명에게만.
                             수신자 무관, last_sent_at 갱신 없음(실발송 이력 아님) -->
                        <b-button size="sm" variant="outline-info" class="ml-auto"
                                  :disabled="draftLoading || isDraftDirty || testSending || enSendBlockedNow"
                                  :title="(enSendBlockedNow || isDraftDirty) ? sendDisabledReason : '현재 로그인 계정으로만 테스트 발송'"
                                  @click="sendTestDraft">
                            <b-spinner v-if="testSending" small class="mr-1"/>
                            발송 테스트
                        </b-button>
                        <b-button size="sm" variant="success"
                                  :disabled="draftLoading || isDraftDirty || !(draft.recipients || []).length || enSendBlockedNow"
                                  :title="sendDisabledReason"
                                  @click="sendDraft">
                            <b-spinner v-if="sending" small class="mr-1"/>
                            {{ alreadySent ? '다시 발송' : '지금 발송' }}
                        </b-button>
                    </div>
                    <div v-if="isDraftDirty" class="text-warning small mt-1">
                        저장하지 않은 편집이 있습니다. [저장 + 미리보기]를 눌러 반영하세요.
                    </div>
                    <div v-if="draft.last_sent_at" class="text-success small mt-1">
                        마지막 발송: {{ formatSentAt(draft.last_sent_at) }}
                    </div>
                </template>
            </b-tab>
        </b-tabs>

        <!-- campaign_id/company_id 가 빠진 링크 — 업체를 모르는 채 요청하지 않도록 버튼 없이 목록으로 -->
        <div v-else-if="invalidLink" class="text-center py-5">
            <div class="text-muted mb-3">잘못된 링크입니다. 목록에서 업체를 선택해 주세요.</div>
            <b-button size="sm" variant="outline-secondary" @click="goBack">목록으로</b-button>
        </div>
        <div v-else-if="jobLoading" class="text-center text-muted py-5">
            <b-spinner small class="mr-2"/>보완검토 이력을 불러오는 중...
        </div>
        <div v-else-if="loadError" class="text-center py-5">
            <div class="text-danger mb-2">보완검토 이력을 불러오지 못했습니다.</div>
            <b-button size="sm" variant="outline-secondary" @click="loadLatestJob">다시 시도</b-button>
        </div>
        <div v-else-if="!creating" class="text-center py-5">
            <div class="text-muted mb-3">
                {{ meta.companyName || '이 업체' }}의 보완검토 이력이 없습니다. 아래 버튼으로 초벌 판정을 시작하세요.
            </div>
            <b-button variant="primary" @click="createJob">새 보완검토 요청</b-button>
        </div>

        <!-- 발송 confirm — 체크박스가 필요해 $showModal 대신 로컬 모달(§13) -->
        <b-modal v-model="sendConfirmOpen" title="메일 발송"
                 :ok-variant="alreadySent ? 'danger' : 'success'"
                 :ok-title="alreadySent ? '다시 발송' : '지금 발송'" cancel-title="취소"
                 :ok-disabled="sendConfirmOkDisabled" @ok="doSendDraft">
            <p v-if="alreadySent" class="text-danger font-weight-bold mb-2">
                이 초안은 {{ formatSentAt(draft.last_sent_at) }}에 이미 발송되었습니다. 아래 수신자에게 다시 발송됩니다.
            </p>
            <p class="mb-1">수신자 {{ sendSummary.total }}명에게 보완요청 메일을 지금 발송합니다.</p>
            <ul class="small mb-2 pl-3" style="max-height: 160px; overflow-y: auto">
                <li v-for="(r, i) in sendSummary.recipients" :key="i">{{ r }}</li>
                <li v-if="sendSummary.moreCount" class="text-muted">외 {{ sendSummary.moreCount }}명</li>
            </ul>
            <p class="small mb-0">제목: {{ sendSummary.subject }}</p>
            <p class="small mb-0">기한: {{ sendSummary.deadline }}</p>
            <p class="small mb-2">언어: {{ localeNotice }}</p>
            <p class="small text-danger mb-2">발송하면 되돌릴 수 없습니다.</p>
            <p v-if="enMissingWarning" class="text-danger mb-2"><AppIcon name="alert-triangle" class="mr-1"/>{{ enMissingWarning }}</p>
            <!-- 영문 발송인데 '번역 없음' 마커가 남았으면 override 체크 전까지 발송 막음(§5) -->
            <b-form-checkbox v-if="enSendNeedsOverride" v-model="allowPlaceholders" class="mb-2">
                '번역 없음' {{ enUnresolvedCount }}개가 남아 있어도 발송
            </b-form-checkbox>
            <b-form-checkbox v-model="reopenAnswers">평가상태를 답변 가능으로 전환</b-form-checkbox>
        </b-modal>

        <!-- 문항 상세 모달 — 대화 패널을 열면 모달 폭이 패널만큼 확장된다
             (본문 영역을 잠식하지 않음). b-modal 은 루트에 class 를 전달하지
             않으므로 dialog-class 로 .modal-dialog 에 직접 얹는다 -->
        <b-modal v-model="detailOpen" size="xl" hide-footer
                 :dialog-class="['sr-detail-dialog', itemChatOpen ? 'sr-chat-expanded' : '']"
                 :title="detail ? `${detail.sub_code} · ${detail.question_group_name || ''}` : ''"
                 @hide="onDetailHide"
                 @shown="scrollItemChat">
            <template v-if="detail">
                <!-- 좌: 문항 상세 본문 / 우: 접이식 문항별 AI 대화 패널 -->
                <div class="sr-detail-layout">
                    <div class="sr-detail-main">
                        <div class="d-flex align-items-center flex-wrap mb-2">
                            <b-badge :variant="statusVariant(detail.current_status)" class="mr-1">
                                현재: {{ statusLabel(detail.current_status) }}
                            </b-badge>
                            <b-badge variant="light">AI 최초: {{ statusLabel(detail.original_status) }}</b-badge>
                            <b-badge v-if="detail.confidence" variant="light">
                                {{ confidenceLabel(detail.confidence) }}
                            </b-badge>
                            <!-- '확인함' — 평가자가 이 문항을 한 번 봤다는 표시. 저장 버튼과 무관하게
                                 즉시 PATCH {reviewer_checked} 단독 전송(판정/이력/메일 포함에 영향 없음) -->
                            <b-form-checkbox v-model="reviewerChecked" switch class="ml-auto"
                                             :disabled="reviewerChecking"
                                             @change="toggleReviewerChecked">
                                확인함
                                <span v-if="detail.reviewer_checked" class="text-muted small ml-1">
                                    {{ formatSentAt(detail.reviewer_checked_at) }} · 사용자 {{ detail.reviewer_checked_by }}
                                </span>
                            </b-form-checkbox>
                        </div>
                        <p class="small">{{ detail.question_text }}</p>
                        <p v-if="detail.supplement_reason" class="small">
                            <strong>판정 사유:</strong> {{ withFileNames(detail.supplement_reason) }}
                        </p>

                        <!-- 답변 ↔ 주장별 판정: 왼쪽=업체 답변/첨부(증빙부족 항목 하이라이트), 오른쪽=주장별 판정 -->
                        <b-row v-if="answerTree" class="mt-2">
                            <b-col md="6">
                                <h6 class="mb-1">답변</h6>
                                <!-- 하이라이트 색상 범례 (증빙 판정 표시) -->
                                <div class="sr-legend mb-2">
                                    <span class="mr-1">증빙 판정 표시:</span>
                                    <span class="sr-chip sr-node-unsupported">증빙 부족</span>
                                    <span class="sr-chip sr-node-supported">증빙 확인</span>
                                    <span class="sr-chip sr-node-exempt">증빙 불요</span>
                                </div>
                                <!-- ⛔ hover 는 mouseover.stop(버블링, 최내측만) + 컨테이너 mouseleave
                                     로 처리한다. mouseenter/leave 는 중첩 노드에서 자식→부모 복귀 시
                                     부모 mouseenter 가 재발화하지 않아 하이라이트가 끊긴다. -->
                                <div class="border rounded p-3 sr-answer-box"
                                     @mouseleave="hoverClaim = null">
                                    <!-- 테이블형 문항 — 열 이름이 곧 행 객체의 키이고 답변 값이
                                         행에 인라인 저장된다 (result 는 항상 빈 배열) -->
                                    <b-table-simple v-if="answerTree.isTable" small bordered
                                                    class="mb-0 sr-answer-table">
                                        <b-thead>
                                            <b-tr>
                                                <b-th v-for="h in answerTree.tableHeader" :key="h">
                                                    {{ h }}
                                                </b-th>
                                            </b-tr>
                                        </b-thead>
                                        <b-tbody>
                                            <b-tr v-for="row in answerTree.tableRows" :key="row.key">
                                                <b-td v-for="cell in row.cells" :key="cell.key"
                                                      :class="cell.editable ? '' : 'sr-table-label'">
                                                    <template v-if="!cell.editable || cell.hasValue">
                                                        {{ cell.value }}
                                                    </template>
                                                    <span v-else class="text-muted small">
                                                        {{ cell.placeholder ? `미입력 (${cell.placeholder})` : '미입력' }}
                                                    </span>
                                                </b-td>
                                            </b-tr>
                                        </b-tbody>
                                    </b-table-simple>
                                    <!-- 답변 옵션 (실제 문항 폼과 동일한 라디오/체크박스 표기) -->
                                    <template v-for="opt in answerTree.options">
                                        <div :key="opt.key"
                                             :class="['sr-node', opt.selected ? nodeClass(opt.claim) : 'sr-node-plain']"
                                             @mouseover.stop="hoverClaim = opt.claim && opt.claim.claim_id">
                                            <b-form-checkbox v-if="answerTree.multi" :checked="opt.selected" disabled>
                                                <span :class="opt.selected ? 'font-weight-500' : 'text-muted'">
                                                    {{ opt.text || opt.value }}</span>
                                                <fa v-if="opt.selected && opt.claim" :icon="verdictIcon(opt.claim)"
                                                    :class="['ml-1', verdictTextClass(opt.claim)]"/>
                                            </b-form-checkbox>
                                            <b-form-radio v-else :checked="answerTree.selectedValue"
                                                          :value="opt.value" disabled>
                                                <span :class="opt.selected ? 'font-weight-500' : 'text-muted'">
                                                    {{ opt.text || opt.value }}</span>
                                                <fa v-if="opt.selected && opt.claim" :icon="verdictIcon(opt.claim)"
                                                    :class="['ml-1', verdictTextClass(opt.claim)]"/>
                                            </b-form-radio>

                                            <!-- 선택한 옵션의 하위 항목 (체크박스 세부항목 / 작성값) -->
                                            <div v-if="opt.selected" class="sr-elements">
                                                <template v-for="el in opt.elements">
                                                    <template v-if="el.type === 'checkbox'">
                                                        <div v-for="it in el.items" :key="it.key"
                                                             :class="['sr-subnode', it.checked ? nodeClass(it.claim) : '']"
                                                             @mouseover.stop="hoverClaim = it.checked && it.claim && it.claim.claim_id">
                                                            <b-form-checkbox :checked="it.checked" disabled>
                                                                <span :class="it.checked ? '' : 'text-muted'">
                                                                    {{ it.text || it.value }}</span>
                                                                <fa v-if="it.checked && it.claim" :icon="verdictIcon(it.claim)"
                                                                    :class="['ml-1', verdictTextClass(it.claim)]"/>
                                                            </b-form-checkbox>
                                                        </div>
                                                    </template>
                                                    <div v-else-if="el.hasValue" :key="el.key"
                                                         :class="['sr-subnode sr-written', nodeClass(el.claim)]"
                                                         @mouseover.stop="hoverClaim = el.claim && el.claim.claim_id">
                                                        <fa icon="pen" class="mr-1 text-secondary"/>
                                                        <span class="text-muted mr-1">{{ el.label || '작성' }}:</span>
                                                        {{ el.displayValue }}
                                                        <fa v-if="el.claim" :icon="verdictIcon(el.claim)"
                                                            :class="['ml-1', verdictTextClass(el.claim)]"/>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </template>

                                    <!-- 해당없음 -->
                                    <div v-if="answerTree.isNa" class="sr-node sr-node-plain">
                                        <b-form-checkbox :checked="true" disabled>
                                            <span class="font-weight-500">해당없음</span>
                                        </b-form-checkbox>
                                        <div class="sr-written small mt-1">
                                            <span class="text-muted mr-1">사유:</span>
                                            <span :class="{'text-danger': !answerTree.naReason}">
                                                {{ answerTree.naReason || '(사유 미작성)' }}</span>
                                        </div>
                                    </div>

                                    <!-- 자유 작성 답변 (옵션 트리에 없는 서술) -->
                                    <div v-for="fc in answerTree.freeClaims" :key="fc.key"
                                         :class="['sr-node sr-written', nodeClass(fc.claim)]"
                                         @mouseover.stop="hoverClaim = fc.claim && fc.claim.claim_id">
                                        <fa icon="pen" class="mr-1 text-secondary"/>{{ fc.displayValue }}
                                        <fa v-if="fc.claim" :icon="verdictIcon(fc.claim)"
                                            :class="['ml-1', verdictTextClass(fc.claim)]"/>
                                    </div>

                                    <!-- 트리에 조인 안 된 서버 주장 (누락 방지 fallback) -->
                                    <div v-for="uc in answerTree.unmatchedClaims" :key="uc.key"
                                         :class="['sr-node sr-written small', nodeClass(uc.claim)]"
                                         @mouseover.stop="hoverClaim = uc.claim && uc.claim.claim_id">
                                        <fa icon="tasks" class="mr-1 text-secondary"/>
                                        <span v-if="uc.label" class="text-muted mr-1">{{ uc.label }}:</span>
                                        {{ uc.displayValue }}
                                        <fa v-if="uc.claim" :icon="verdictIcon(uc.claim)"
                                            :class="['ml-1', verdictTextClass(uc.claim)]"/>
                                    </div>

                                    <div v-if="answerTree.empty" class="text-muted small">
                                        제출된 답변 내용이 없습니다.
                                    </div>
                                </div>

                                <!-- 첨부 (클릭 시 다운로드) -->
                                <h6 class="mt-3 mb-1">첨부자료 ({{ attachments.length }})</h6>
                                <div v-if="attachments.length" class="border rounded p-2 sr-answer-box">
                                    <div v-for="att in attachments" :key="att.storage_id"
                                         :class="['sr-att', {'sr-att-cited': citedStorageIds.has(att.storage_id)}]">
                                        <b-link class="sr-att-link" :disabled="downloadingId === att.storage_id"
                                                @click.prevent="downloadAttachment(att)">
                                            <b-spinner v-if="downloadingId === att.storage_id" small class="mr-1"/>
                                            <fa v-else icon="file-download" class="mr-1"/>{{ att.filename }}
                                        </b-link>
                                        <b-badge v-if="citedStorageIds.has(att.storage_id)"
                                                 variant="info" pill class="ml-1">근거로 인용됨</b-badge>
                                    </div>
                                </div>
                                <div v-else class="text-muted small">이 문항에 제출된 첨부자료가 없습니다.</div>
                            </b-col>

                            <b-col md="6">
                                <h6>주장별 판단</h6>
                                <!-- 좌측 "답변" 표시 순서대로 정렬된 카드 —
                                     entry.group(원본 인덱스)으로 hover 연동 유지 -->
                                <div v-if="claimJudgements.length" class="sr-claim-list">
                                    <div v-for="entry in orderedClaimJudgements" :key="entry.group"
                                         :class="['sr-claim-card', cardClass(entry.cj), {'sr-claim-hover': hoverGroup === entry.group}]"
                                         @mouseenter="hoverClaim = (entry.cj.claim_ids || [])[0] || null"
                                         @mouseleave="hoverClaim = null">
                                        <div class="d-flex align-items-start">
                                            <b-badge :variant="cjBadge(entry.cj).variant" class="mr-2 mt-1">
                                                {{ cjBadge(entry.cj).text }}
                                            </b-badge>
                                            <div class="flex-grow-1 small">
                                                <div>{{ entry.cj.claim }}</div>
                                                <div class="text-muted mt-1">{{ withFileNames(entry.cj.reason) }}</div>
                                                <div v-if="entry.cj.needs_evidence === false && entry.cj.exemption_reason"
                                                     class="text-muted mt-1">면제 사유: {{ withFileNames(entry.cj.exemption_reason) }}</div>
                                                <div v-if="entry.cj.evidence_note" class="text-muted mt-1">
                                                    근거: {{ withFileNames(entry.cj.evidence_note) }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="text-muted small">
                                    구조 규칙으로 판정된 문항입니다. (개별 주장 판정 없음)
                                </div>
                                <div v-if="decisionRationale.length" class="small text-muted mt-2">
                                    <div v-for="(r, i) in decisionRationale" :key="i">· {{ withFileNames(r) }}</div>
                                </div>
                            </b-col>
                        </b-row>

                        <!-- 구 형식(평가요소 축) 판정 fallback — 재설계 이전 잡 데이터 호환 -->
                        <template v-else-if="detail.judgement && detail.judgement.criterion_judgements">
                            <h6 class="mt-3">평가요소별 판단</h6>
                            <b-table-simple small bordered>
                                <b-thead>
                                    <b-tr>
                                        <b-th>평가요소</b-th>
                                        <b-th style="width:9%">충족</b-th>
                                        <b-th style="width:41%">근거</b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr v-for="cj in detail.judgement.criterion_judgements" :key="cj.ev_id">
                                        <b-td class="small">{{ cj.criterion_name }}</b-td>
                                        <b-td class="text-center">
                                            <b-badge :variant="cj.satisfied === true ? 'success'
                                                         : cj.satisfied === false ? 'danger' : 'secondary'">
                                                {{ cj.satisfied === true ? '충족' : cj.satisfied === false ? '미충족' : '판단불가' }}
                                            </b-badge>
                                        </b-td>
                                        <b-td class="small">{{ cj.reason }}</b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                            <div v-if="(detail.judgement.decision_rationale || []).length" class="small text-muted">
                                <div v-for="(r, i) in detail.judgement.decision_rationale" :key="i">· {{ r }}</div>
                            </div>
                        </template>

                        <template v-if="(detail.overrides || []).length">
                            <h6 class="mt-3">Override 이력</h6>
                            <div v-for="(ov, i) in detail.overrides" :key="i" class="small text-muted">
                                · {{ ov.created_at }} — {{ statusLabel(ov.from_status) }} →
                                {{ statusLabel(ov.to_status) }} ({{ ov.reason }})
                            </div>
                        </template>

                        <!-- 내부 전용 — 업체 발송 원고와 분리된 AI 개선용 기록 -->
                        <template v-if="(detail.notes || []).length">
                            <h6 class="mt-3">AI 개선용 피드백 이력 <span class="text-muted small">(내부 전용)</span></h6>
                            <div v-for="(n, i) in detail.notes" :key="`note-${i}`" class="small text-muted">
                                · {{ n.created_at }} —
                                <b-badge :variant="n.status_changed ? 'warning' : 'light'">
                                    {{ n.status_changed ? '판정 변경함' : '판정 유지' }}
                                </b-badge>
                                <span class="ml-1">{{ n.note }}</span>
                            </div>
                        </template>

                        <hr>
                        <h6>평가자 확정 / 수정</h6>
                        <b-row>
                            <b-col md="4">
                                <b-form-group label="상태 변경" label-size="sm">
                                    <b-form-select v-model="editStatus" size="sm" :options="statusOptions"/>
                                </b-form-group>
                                <b-form-group label="AI 개선용 피드백 (내부 전용)" label-size="sm">
                                    <b-form-textarea v-model="editReason" rows="3" size="sm"
                                                     placeholder="AI 판정의 문제점·개선점을 남겨주세요. 업체에는 발송되지 않습니다."/>
                                    <div class="text-muted small mt-1">
                                        업체에 발송되지 않는 내부 기록입니다. 상태를 바꾸지 않아도 저장되며,
                                        상태를 변경할 때는 필수입니다.
                                    </div>
                                </b-form-group>
                                <b-form-group label="메일 포함" label-size="sm">
                                    <b-form-select v-model="editInclude" size="sm"
                                                   :options="[{value: null, text: '자동 (보완 필요면 포함)'},
                                                              {value: true, text: '포함'},
                                                              {value: false, text: '제외'}]"/>
                                </b-form-group>
                            </b-col>
                            <b-col md="8">
                                <b-form-group label="보완요청 피드백 문구 (업체 발송 · 메일 표에 들어감)"
                                              label-size="sm">
                                    <b-form-textarea v-model="editFeedback" rows="6" size="sm"/>
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <div class="text-right">
                            <b-button size="sm" variant="primary" :disabled="saving" @click="saveItem">
                                <b-spinner v-if="saving" small class="mr-1"/>저장
                            </b-button>
                        </div>
                    </div>
                    <!-- 우측 접이식 문항별 AI 대화 패널 — 스레드 단위 (job_id, item_id), 잡 전체 대화 탭과 분리 -->
                    <div :class="['sr-chat-panel', {'sr-chat-open': itemChatOpen}]">
                        <button type="button" class="sr-chat-handle"
                                :title="itemChatOpen ? 'AI 대화 패널 접기' : 'AI 대화 패널 펼치기'"
                                :aria-expanded="String(itemChatOpen)"
                                @click="toggleItemChat">
                            <span aria-hidden="true" class="sr-chat-arrow">
                                <b-icon :icon="itemChatOpen ? 'chevron-double-right' : 'chevron-double-left'"/>
                            </span>
                            <span class="sr-chat-handle-label">AI 대화<template v-if="detailTurns.length"> ({{ detailTurns.length }})</template></span>
                        </button>
                        <!-- v-show: v-if 로 하면 펼칠 때마다 재마운트되어 ref/스크롤
                             타이밍이 흔들린다 — DOM 에 상주시키고 표시만 토글 -->
                        <div v-show="itemChatOpen" class="sr-chat-body">
                            <h6 class="mb-2">이 문항 AI 대화 <span class="text-muted small">({{ detailTurns.length }})</span></h6>
                            <div v-if="!isJobCompleted" class="text-muted small">
                                판정이 완료된 잡에서만 대화할 수 있습니다.
                            </div>
                            <template v-else>
                                <!-- column-reverse: 브라우저가 스크롤을 하단(최신 턴)에
                                     네이티브로 고정한다 — 마운트/접힘 토글에도 안전.
                                     DOM 은 최신→과거 순으로 두고 시각적으론 과거→최신. -->
                                <div ref="itemChatBox" class="chat-box sr-chat-scroll border rounded p-2 mb-2">
                                    <div v-if="itemChatting" class="text-muted small">
                                        <b-spinner small class="mr-1"/> 답변 생성 중…
                                    </div>
                                    <div v-for="turn in detailTurnsDesc" :key="turn.id"
                                         :class="['mb-2', turn.role === 'user' ? 'text-right' : '']">
                                        <div :class="['d-inline-block px-2 py-1 rounded',
                                                      turn.role === 'user' ? 'bg-primary text-white' : 'bg-light']"
                                             style="max-width: 90%; white-space: pre-wrap; text-align: left">
                                            {{ turn.content }}
                                        </div>
                                    </div>
                                    <div v-if="detailTurns.length === 0" class="text-muted small">
                                        이 문항의 판정에 대해 질문해 보세요. (예: "왜 보완 필요로 판정됐어?")
                                        <br>대화는 설명·재검토 조언만 제공합니다 — 판정 변경은 평가자 확정/수정으로 하세요.
                                    </div>
                                </div>
                                <b-input-group size="sm">
                                    <b-form-input v-model="itemChatInput"
                                                  :disabled="itemChatting"
                                                  placeholder="이 문항에 대한 질문 입력 후 Enter"
                                                  @keydown.enter.prevent="onItemChatEnter"/>
                                    <b-input-group-append>
                                        <b-button variant="primary"
                                                  :disabled="itemChatting || !itemChatInput.trim()"
                                                  @click="sendItemChat">전송</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </template>
                        </div>
                    </div>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
// AI 보완검토 화면 — 업체×캠페인 초벌 판정 요청/검토/override/대화/메일 초안.
// 백엔드: /api/supplement-review (RBAC supplement:review:manage — admin level 3 이상).
// 판정 파이프라인은 비동기(Celery)라 폴링으로 진행률을 갱신한다.

// 순수 로직(키 재계산/dirty 스냅샷/행 정렬)은 테스트 가능한 헬퍼로 분리한다.
import draftHelper from './supplementEmailDraft';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import HtmlSplitEditor from '@shared/components/admin/pages/Management/Mail/MailEditor/_SubComponent/HtmlSplitEditor.vue';

const ITEM_STATUS_OPTIONS = [
    {value: 'NO_SUPPLEMENT', text: '보완 불필요'},
    {value: 'SUPPLEMENT_REQUIRED', text: '보완 필요'},
    {value: 'REVIEW_REQUIRED', text: '평가자 확인'},
    {value: 'PROCESSING_FAILED', text: '처리 실패'},
];

// URL query 값 — 크래프트된 링크는 같은 키를 반복해 배열이 될 수 있어 첫 값, 문자열만 쓴다
// (SupplementReviewList initFromQuery 와 같은 규칙).
function queryStr(query, key) {
    const v = Array.isArray(query[key]) ? query[key][0] : query[key];
    return typeof v === 'string' ? v : '';
}

// 문항 판정 필터 — 화이트리스트 밖 값은 '전체'
function queryStatus(query, key) {
    const v = queryStr(query, key);
    return ITEM_STATUS_OPTIONS.some((o) => o.value === v) ? v : 'ALL';
}

const RECIPIENT_ERRORS = {
    empty: '추가할 이메일을 입력해 주세요.',
    duplicate: '이미 목록에 있는 주소입니다.',
    invalid: '올바른 이메일 형식이 아닙니다.',
};

// 구조 규칙 rationale 고정 문구(백엔드 _supplement_rules.py) → 판정 근거 뱃지.
// 목록 API 가 rule_id 를 내려주지 않아 사유 문구 매핑으로 판별한다 — 문구가
// 바뀌면 AI 분석 뱃지로 폴백되므로 깨지지 않는다. rule_id 노출은 백엔드
// #1511 에 요청됨(오면 이 매핑을 rule_id 기준으로 교체).
const VERDICT_BASIS_RULES = [
    {m: '평가 대상 문항의 답변이 비어 있음', text: '답변 없음'},
    {m: '첨부 필수 문항의 긍정 답변인데 이 문항에 첨부자료가 없음', text: '첨부 없음'},
    {m: '해당없음 선택 + 사유 미작성', text: 'N/A 사유 없음'},
    {m: '부모 답변만 선택하고 하위 세부항목을 하나도 선택하지 않음', text: '하위 미선택'},
    {m: '하위 세부항목 미선택 + 첨부 필수', text: '하위·첨부 없음'},
    {m: '답변 완료 + 평가요소 없음', text: '검토 대상 아님'},
    {m: '패킷 생성 오류', text: '처리 오류'},
];

// 보완요청 메일 제목/본문은 이메일 템플릿 관리(시나리오 [보완요청])에서 가져온다.
// 템플릿·영문 내용이 없을 때 관리자를 보낼 곳 (admin level 3 메뉴, router.js MailDashboard).
// 편집기(/admin/mail/editor)가 아니라 목록 — 신규 등록·기존 템플릿 영문 추가 양쪽의 진입점이다.
const MAIL_TEMPLATE_URL = '/admin/mail';

// 영문 발송 차단 사유(BE en_send_blocked_reason) → 화면 문구. 여기 없는 코드는 campaign_name 으로 취급.
const EN_BLOCK_MESSAGES = {
    campaign_name: '캠페인 영문명을 등록해주세요.',
    template_en_missing: '이 프로젝트/기본 템플릿에 영문 본문이 없습니다. '
        + '이메일 템플릿 관리에서 영문(en) 내용을 추가하세요.',
};

export default {
    name: 'SupplementReview',
    components: {HtmlSplitEditor},
    // '← 목록'을 history.back() 으로 할지 — 목록에서 들어온 경우만(새 탭·딥링크는 목록으로 push)
    beforeRouteEnter(to, from, next) {
        next((vm) => { vm.cameFromList = from.name === 'ManagingSupplementReviewList'; });
    },
    data() {
        // 보기 상태 복원(탭·필터·정렬·초안 언어·문항) — 키는 syncViewQuery 가 쓰는 것과 같다
        const q = this.$route.query;
        const sortKey = queryStr(q, 'sort');
        const hasSort = ['original', 'current'].includes(sortKey);
        return {
            campaignId: Number(this.$route.query.campaign_id) || null,
            companyId: Number(this.$route.query.company_id) || null,
            invalidLink: false,  // campaign_id/company_id 가 빠진 링크
            cameFromList: false,
            // 헤더 표시용 이름 — campaign_id/company_id 만으로는 알 수 없어 별도 조회
            meta: {projectCode: null, companyName: null, campaignName: null, period: null},
            job: null,
            items: [],
            reruningIds: [],  // 재판정 요청 전송 중인 문항 item_id (버튼 spinner/disabled)
            reruningAll: false,  // 실패 문항 일괄 재판정 전송 중 (버튼 spinner/disabled)
            // 문항 판정 테이블 뷰 상태 (원본 items 는 건드리지 않음)
            itemFilter: {original: queryStatus(q, 'original'), current: queryStatus(q, 'current')},
            // key: 'original'|'current'|null
            itemSort: {key: hasSort ? sortKey : null, dir: hasSort && queryStr(q, 'dir') === 'desc' ? -1 : 1},
            // 판정 표 보기 — 'group'(문항별, 기본) | 'list'(전체 목록)
            itemView: queryStr(q, 'view') === 'list' ? 'list' : 'group',
            creating: false,
            jobLoading: true,   // 최초 이력 조회 중 — '이력 없음' 문구가 깜빡이지 않도록
            loadError: false,   // 이력 조회 실패 — '이력 없음'과 구분
            pollTimer: null,
            pollFailCount: 0,
            pollError: false,
            // 상세/override
            detailOpen: false,
            detail: null,
            // 링크(?item=)로 열 문항 — 잡을 불러온 뒤 한 번 열고 비운다. 그 전까지 URL 에서 지우지 않는다.
            pendingItemId: Number(queryStr(q, 'item')) || null,
            openingItemId: null,  // 상세를 불러오는 중인 행(스피너)
            detailReqSeq: 0,      // 연속 클릭 시 마지막 요청 응답만 쓰기 위한 순번
            jobReqSeq: 0,         // loadJob 마지막 요청 순번(늦게 온 이전 응답 버림)
            skipHideGuard: false, // 저장 성공·닫기 확인 뒤 닫을 때는 미저장 가드를 건너뛴다
            hoverClaim: null,  // 답변 노드 ↔ 주장 카드 연동 하이라이트용 claim_id
            downloadingId: null,  // 다운로드 중인 첨부 storage_id
            // 문항별 대화 (모달 우측 접이식 패널) — 히스토리는 detail.turns 에 붙는다
            itemChatInput: '',
            itemChatting: false,
            itemChatOpen: false,  // 패널 접힘/펼침 — openDetail 시 대화 이력 유무로 재설정
            editStatus: null,
            editReason: '',
            editInclude: null,
            editFeedback: '',
            saving: false,
            // '확인함' 스위치 — v-model 로컬값(실패 시 롤백용) + 요청 중 플래그
            reviewerChecked: false,
            reviewerChecking: false,
            // 메일 초안 — 탭은 진행 중 잡이면 applyJob 이 문항 판정 탭으로 되돌린다
            activeTab: queryStr(q, 'tab') === 'draft' ? 1 : 0,
            panelCollapsed: false,
            recipientsExpanded: false,  // 수신자 목록 펼침(제거/추가는 펼쳐야 가능)
            draft: null,
            draftLoading: false,
            draftLoadError: false,  // 초안 조회 실패(404 '미생성'과 구분)
            deadlineDate: '',
            deadlineTime: '',   // 비우면 날짜만 저장 (기본값 강제 없음)
            supplementVariables: [
                {key: '{{ campaign_name }}', label: '캠페인명'},
                {key: '{{ company_name }}', label: '업체명'},
                {key: '{{ supplement_deadline }}', label: '보완 기한'},
                {key: '{{ system_url }}', label: '시스템 URL'},
            ],
            previewRefreshKey: 0,
            savedSnapshot: '',  // 마지막 저장/로드 시점 편집필드 스냅샷 (dirty 판정 기준)
            // 본문 알약/미리보기에 쓰는 변수 값 — 저장/로드 시점 스냅샷, 로케일별({ko, en}).
            // 입력란만 바꿔서는 본문이 바뀌지 않고 [저장 + 미리보기] 를 눌러야 반영된다.
            appliedVariableValues: {ko: null, en: null},
            newRecipientEmail: '',  // 임의 수신자 추가 입력값
            recipientError: '',     // 수신자 추가 입력 오류 문구(입력은 남긴다)
            sending: false,     // 발송 진행 중(버튼 spinner/disabled)
            testSending: false, // 발송 테스트 진행 중(중복 클릭 방지)
            // 영문 번역 — 제목/본문만 로케일별(subject_en/body_html_en), 나머지는 공유
            // 편집 중인 로케일 ('ko'|'en') — 발송/테스트 언어도 이 값. ?draft_lang=en 은 영문 초안이
            // 있을 때만 유지된다(loadDraftSilently 가 없으면 ko 로 되돌림).
            activeLocale: queryStr(q, 'draft_lang') === 'en' ? 'en' : 'ko',
            translating: false,     // AI 번역 요청 중
            // §13 발송 confirm — 발송과 함께 평가상태를 답변 가능으로 되돌릴지(기본 on)
            sendConfirmOpen: false,
            reopenAnswers: true,
            // 영문 '번역 없음' override — confirm 열 때마다 false 로 초기화(§5)
            allowPlaceholders: false,
        };
    },
    computed: {
        isJobActive() {
            return !!this.job && ['PENDING', 'RUNNING', 'FINALIZING'].includes(this.job.status);
        },
        isJobCompleted() {
            return !!this.job && ['DONE', 'PARTIAL'].includes(this.job.status);
        },
        // 처리 실패(FAILED) 문항 전체(필터 무관) — 일괄 재판정 대상.
        failedItems() {
            return (this.items || []).filter((i) => i.processing_status === 'FAILED');
        },
        // 헤더 라벨: "{프로젝트코드} / {업체명} · {캠페인명}[ / Job #N]" — 잡이 없어도 표시.
        // meta 미로딩/조회실패 시 ID 표기로 폴백한다. 잡은 loadLatestJob 이 이 업체 것만 연다.
        headerLabel() {
            if (!this.campaignId || !this.companyId) return '';
            const proj = this.meta.projectCode || `캠페인 ${this.campaignId}`;
            const company = this.meta.companyName || `업체 ${this.companyId}`;
            const mid = this.meta.campaignName
                ? `${company} · ${this.meta.campaignName}` : company;
            return this.job ? `${proj} / ${mid} / Job #${this.job.job_id}` : `${proj} / ${mid}`;
        },
        // 문항 상세 모달의 저장하지 않은 수정 — 닫기·이탈 가드 공용
        itemDirty() {
            const d = this.detail;
            if (!this.detailOpen || !d) return false;
            return this.editStatus !== d.current_status
                || !!this.editReason.trim()
                || this.editFeedback !== (d.feedback_text || '')
                || this.editInclude !== d.include_in_email;
        },
        // 초안 이후 문항 판정·피드백이 바뀌어 메일 표와 어긋났는지 — 새로고침해도 다시 계산된다
        draftOutdated() {
            return !!this.draft && draftHelper.isDraftRowsOutdated(this.draft.rows, this.items);
        },
        // 문항이 0건일 때 표 안내 — 진행 중 / 실패 / 대상 없음 구분
        emptyItemsMessage() {
            if (this.isJobActive) return '문항 판정을 준비하는 중입니다.';
            if (this.job && this.job.status === 'FAILED') {
                const reason = draftHelper.jobErrorLabel(this.job.error_code);
                return `처리에 실패했습니다${reason ? `: ${reason}` : '.'} 위의 [새 보완검토 요청]으로 다시 요청할 수 있습니다.`;
            }
            return '판정 대상 문항이 없습니다.';
        },
        // 보완 기한 선택 하한(로컬 오늘) — 지난 날짜가 기한으로 나가지 않도록
        todayYmd() {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },
        // 저장 대비 dirty 판정 — 편집 가능한 필드(제목/수신/기한/URL/HTML/행)만 직렬화.
        draftSnapshot() {
            return draftHelper.serializeDraftFields(this.draft);
        },
        isDraftDirty() {
            return !!this.draft && this.draftSnapshot !== this.savedSnapshot;
        },
        // 로케일별 편집 필드 — 제목/본문만 분리, 기한/URL/수신자/표는 공유
        subjectField() {
            return this.activeLocale === 'en' ? 'subject_en' : 'subject';
        },
        bodyField() {
            return this.activeLocale === 'en' ? 'body_html_en' : 'body_html';
        },
        // 영문 존재의 단일 기준 = body_html_en (제목은 보조, 비면 국문 제목으로 발송)
        hasEn() {
            return !!((this.draft && this.draft.body_html_en) || '').trim();
        },
        // 미리보기용 치환본 — 소스/저장본은 토큰 그대로, 미리보기에만 저장 시점 값이 들어간다.
        previewBodyHtml() {
            if (!this.draft) return '';
            return draftHelper.substituteWithValues(
                this.draft[this.bodyField], this.appliedVariableValues[this.activeLocale]);
        },
        // 발송/테스트 언어 = 현재 보고 있는 로케일. 영문 탭인데 영문이 없으면 국문으로.
        effectiveLocale() {
            return (this.activeLocale === 'en' && this.hasEn) ? 'en' : 'ko';
        },
        // §11: 영문 탭 + 영문 초안 존재 + 영문 발송 차단 사유 → 지금 발송/발송 테스트 차단
        enSendBlockedNow() {
            return this.activeLocale === 'en' && this.hasEn && !!(this.draft && this.draft.en_send_blocked);
        },
        // §12 차단 사유 — BE 가 아직 구 문자열('캠페인 영문명 미등록')을 주므로 양쪽 호환.
        // 알려진 코드만 그대로 쓰고, 나머지 사유는 캠페인 영문명으로 취급.
        enBlockedReason() {
            const raw = (this.draft && this.draft.en_send_blocked_reason) || '';
            return EN_BLOCK_MESSAGES[raw] ? raw : 'campaign_name';
        },
        enBlockedMessage() {
            return EN_BLOCK_MESSAGES[this.enBlockedReason] || EN_BLOCK_MESSAGES.campaign_name;
        },
        // 영문 차단이 템플릿 쪽 원인이면 이메일 템플릿 관리로 보낸다(질문지 번역과 목적지가 다름).
        enBlockedTemplateUrl() {
            return this.enBlockedReason === 'template_en_missing' ? MAIL_TEMPLATE_URL : '';
        },
        // 질문지 번역 화면 URL — template_code 기반. '번역 없음' 안내(배지·모달)가 이 한 곳에서만
        // 경로를 조립한다(중복 시 한쪽만 고치는 사고 방지).
        surveyTranslationUrl() {
            const code = this.draft && this.draft.template_code;
            return code ? `/editing_template_translation/${code}` : '';
        },
        // 현재 편집본(bodyField)에 손대지 않은 '번역 없음' 마커(§3)가 몇 개 남았는지 — 라이브.
        // 배지·영문 발송 경고·override 게이트 공용. 저장본 draft.en_unresolved_placeholders 는
        // 참고용(둘이 다르면 dirty → 기존 dirty 게이트가 '저장 후 발송'으로 막는다).
        enUnresolvedCount() {
            if (!this.draft) return 0;
            return draftHelper.countUnresolvedEnPlaceholders(this.draft[this.bodyField]);
        },
        // 영문 로케일에서 '번역 없음' 마커가 남았으면 발송/테스트 confirm 에 띄우는 빨간 경고.
        enMissingWarning() {
            if (this.activeLocale !== 'en') return '';
            const n = this.enUnresolvedCount;
            return n > 0
                ? `영문 표에 '번역 없음' 항목이 ${n}개 있습니다. 그대로 발송하시겠습니까?` : '';
        },
        // 영문 실발송인데 마커가 남아 override 체크가 필요한 상태(§5). 테스트 발송은 무게이트.
        enSendNeedsOverride() {
            return this.effectiveLocale === 'en' && this.enUnresolvedCount > 0;
        },
        // '지금 발송' 비활성 — override 필요한데 아직 체크 안 했으면 막는다(§5).
        sendConfirmOkDisabled() {
            return draftHelper.sendOkDisabled(this.enSendNeedsOverride, this.allowPlaceholders);
        },
        // confirm 창의 언어 안내 1줄
        localeNotice() {
            if (this.effectiveLocale === 'en') return '현재 영문 초안으로 발송됩니다.';
            if (this.activeLocale === 'en') return '영문 초안이 없어 국문으로 발송됩니다.';
            return '현재 국문 초안으로 발송됩니다.';
        },
        // 발송 confirm 요약 — 수신자·실제 나갈 제목·기한(실제 발송 언어 기준)
        sendSummary() {
            return draftHelper.buildSendSummary(this.draft, this.effectiveLocale);
        },
        alreadySent() {
            return !!(this.draft && this.draft.last_sent_at);
        },
        // '지금 발송' 버튼 비활성 사유 툴팁
        sendDisabledReason() {
            if (this.enSendBlockedNow) return `영문 발송 불가 — ${this.enBlockedMessage}`;
            if (this.isDraftDirty) return '저장하지 않은 편집이 있습니다. 먼저 [저장 + 미리보기]를 눌러 주세요.';
            if (!(this.draft && (this.draft.recipients || []).length)) return '수신자가 없습니다. 수신자를 추가해 주세요.';
            return '';
        },
        summary() {
            return this.job && this.job.summary ? this.job.summary.summary : null;
        },
        crossIssues() {
            return (this.job && this.job.summary && this.job.summary.cross_sub_item_issues) || [];
        },
        statusOptions() {
            return ITEM_STATUS_OPTIONS;
        },
        // 문항 판정 테이블 표시용 — AI 판정/현재 상태 필터(받은 순서 그대로). 정렬 · 묶음은 visibleGroups
        visibleItems() {
            let list = this.items;
            if (this.itemFilter.original !== 'ALL') {
                list = list.filter((it) => it.original_status === this.itemFilter.original);
            }
            if (this.itemFilter.current !== 'ALL') {
                list = list.filter((it) => it.current_status === this.itemFilter.current);
            }
            return list;
        },
        // 문항별: 받은 순서에서 연속된 같은 문항(대분류 · 문항 그룹)을 한 묶음으로, 정렬은 묶음 안에서만.
        // 전체 목록: 머리 줄 없는 묶음 하나, 정렬은 표 전체
        visibleGroups() {
            if (this.itemView === 'list') {
                return [{key: 'all', label: null, items: this.sortItems(this.visibleItems)}];
            }
            const groups = [];
            for (const item of this.visibleItems) {
                const major = item.major_category || '';
                const name = item.question_group_name || '';
                const last = groups[groups.length - 1];
                if (last && last.major === major && last.name === name) {
                    last.items.push(item);
                } else {
                    groups.push({key: `g-${item.item_id}`, major, name, label: this.questionGroupLabel(item), items: [item]});
                }
            }
            for (const group of groups) group.items = this.sortItems(group.items);
            return groups;
        },
        // 전체 목록 보기는 문항 칸이 하나 더 있다
        itemColspan() {
            return this.itemView === 'list' ? 8 : 7;
        },
        // 문항별 대화 히스토리 — GET items/{id} 응답의 turns
        detailTurns() {
            return (this.detail && this.detail.turns) || [];
        },
        // column-reverse 렌더용 최신→과거 순 (시각적으로는 과거→최신이 된다)
        detailTurnsDesc() {
            return [...this.detailTurns].reverse();
        },
        // ── 주장-증빙 상세 (판정시점 서버 스냅샷 기반) ──────────────────
        judgement() {
            return (this.detail && this.detail.judgement) || null;
        },
        claimJudgements() {
            return (this.judgement && this.judgement.claim_judgements) || [];
        },
        serverClaims() {
            return (this.judgement && this.judgement.server_claims) || [];
        },
        decisionRationale() {
            return (this.judgement && this.judgement.decision_rationale) || [];
        },
        attachments() {
            return (this.judgement && this.judgement.attachments_snapshot) || [];
        },
        // storage_id → 파일명 (판정시점 서버 스냅샷) — LLM 자유 텍스트 치환용
        attachmentNameById() {
            const map = {};
            this.attachments.forEach((att) => {
                const n = Number(att.storage_id);
                if (!Number.isNaN(n) && att.filename) map[n] = att.filename;
            });
            return map;
        },
        citedStorageIds() {
            // claim_judgements 가 근거로 인용한 storage_id 집합 (첨부 하이라이트용)
            const s = new Set();
            this.claimJudgements.forEach((cj) => {
                (cj.cited_storage_ids || []).forEach((id) => {
                    const n = Number(id);
                    if (!Number.isNaN(n)) s.add(n);
                });
            });
            return s;
        },
        // claim_id → { cj, group } — 주장 판정 커버리지 역인덱스 (묶음 판정은 같은 group)
        verdictByClaimId() {
            const map = {};
            this.claimJudgements.forEach((cj, idx) => {
                (cj.claim_ids || []).forEach((id) => {
                    map[id] = {cj, group: idx};
                });
            });
            return map;
        },
        hoverGroup() {
            const v = this.hoverClaim != null ? this.verdictByClaimId[this.hoverClaim] : null;
            return v ? v.group : null;
        },
        // 좌측 답변 트리의 렌더 순서 — claim_id → 순번 (우측 주장 카드 정렬용)
        claimOrderIndex() {
            const order = {};
            let seq = 0;
            const put = (claim) => {
                if (claim && claim.claim_id != null && order[claim.claim_id] === undefined) {
                    seq += 1;
                    order[claim.claim_id] = seq;
                }
            };
            const t = this.answerTree;
            if (!t) return order;
            t.options.forEach((opt) => {
                put(opt.claim);
                opt.elements.forEach((el) => {
                    if (el.type === 'checkbox') {
                        el.items.forEach((it) => put(it.claim));
                    } else {
                        put(el.claim);
                    }
                });
            });
            t.freeClaims.forEach((f) => put(f.claim));
            t.unmatchedClaims.forEach((u) => put(u.claim));
            return order;
        },
        // 우측 주장 카드를 좌측 "답변" 표시 순서대로 정렬.
        // group 은 claimJudgements 의 원본 인덱스 — hover 연동(verdictByClaimId 의
        // group)과 어긋나지 않도록 정렬 후에도 원본 인덱스를 유지한다.
        orderedClaimJudgements() {
            const order = this.claimOrderIndex;
            const rank = (cj) => {
                const idxs = (cj.claim_ids || [])
                    .map((id) => order[id])
                    .filter((v) => v !== undefined);
                // 좌측 트리에 없는 주장(묶음/미조인)은 뒤로 — 원본 순서 유지
                return idxs.length ? Math.min(...idxs) : Infinity;
            };
            return this.claimJudgements
                .map((cj, group) => ({cj, group, rank: rank(cj)}))
                .sort((a, b) => (a.rank - b.rank) || (a.group - b.group));
        },
        // 답변 스냅샷 → 렌더 트리 (각 노드에 주장 판정 verdict 부착)
        answerTree() {
            const j = this.judgement;
            if (!j || !j.answer_snapshot) return null;
            const snap = j.answer_snapshot;
            const resultVals = this.asArray(snap.result);
            const structure = Array.isArray(snap.structure) ? snap.structure : [];
            const naReason = snap.na_reason || null;
            // N/A 감지 — 백엔드 derive_claims 와 동일(result == na_label). 기본 라벨
            // '해당없음'이 canonical(header enable_na_label override 는 드묾).
            // 사유 유무와 무관하게 N/A 로 인식해 '미응답'과 구분한다.
            const NA_LABEL = '해당없음';
            const isNa = j.rule_id === 'struct-na-without-reason'
                || (resultVals.length === 1 && resultVals[0] === NA_LABEL)
                || (!!naReason && this.serverClaims.length === 0);

            // ── 테이블형 문항 (예: ST1 기본정보) ─────────────────────
            // 감지: table_header 가 있고 행에 옵션 키(value/text)가 없다.
            // 값은 structure[i][열키] 에 인라인 저장되고 result 는 항상 빈
            // 배열이다 (staging df_subs 실데이터로 확인한 계약).
            const tableHeader = Array.isArray(snap.table_header)
                ? snap.table_header.filter((h) => typeof h === 'string' && h) : [];
            const isTable = !isNa && tableHeader.length > 0 && structure.length > 0
                && structure.every((row) => row && typeof row === 'object'
                    && row.value === undefined && row.text === undefined);
            if (isTable) {
                const tableRows = structure.map((row, i) => ({
                    key: `row-${i}`,
                    cells: tableHeader.map((h) => {
                        const editable = !((row.noteditables || []).includes(h));
                        const raw = row[h];
                        const hasValue = editable
                            && raw != null && String(raw).trim() !== '';
                        return {
                            key: `row-${i}-${h}`, editable, hasValue,
                            value: raw != null ? String(raw) : '',
                            placeholder: String((row.placeholder || {})[h] || ''),
                        };
                    }),
                }));
                const anyValue = tableRows.some(
                    (r) => r.cells.some((c) => c.hasValue));
                // 테이블 셀은 아직 주장(claim) 조인이 없다(백엔드 derive_claims
                // 미지원) — 서버 주장이 생기면 누락 없이 fallback 목록에 표시.
                const unmatchedClaims = this.serverClaims.map((c, idx) => ({
                    key: `unmatched-${idx}`,
                    label: c.label || c.path || null,
                    displayValue: this.shortVal(c.value),
                    claim: this.attachVerdict(c.claim_id),
                }));
                return {
                    isNa: false, naReason, isTable: true, tableHeader, tableRows,
                    options: [], freeClaims: [], unmatchedClaims,
                    multi: false, selectedValue: null, selectedValues: [],
                    hasOptions: false,
                    empty: !anyValue && unmatchedClaims.length === 0,
                };
            }

            // 좌측에 조인된 claim_id 추적 — 어떤 것도 누락 없이 표시하기 위함
            const rendered = new Set();
            const track = (claim) => {
                if (claim && claim.claim_id != null) rendered.add(claim.claim_id);
                return claim;
            };

            const options = structure.map((opt, i) => {
                const value = opt && opt.value;
                const selected = resultVals.includes(value);
                const elements = ((opt && opt.element_contents) || []).map((ec, j2) => {
                    const path = `structure[${i}].element_contents[${j2}]`;
                    const type = String((ec && ec.type) || '').toLowerCase();
                    if (type === 'checkbox') {
                        const checkedVals = this.asArray(ec.result);
                        const items = ((ec && ec.structure) || []).map((it, k) => {
                            const iv = it && it.value;
                            const checked = checkedVals.includes(iv);
                            return {
                                key: `${path}-${k}`, text: it && it.text, value: iv, checked,
                                claim: checked
                                    ? track(this.claimByPath(`${path}.structure[${k}]`)) : null,
                            };
                        });
                        return {key: path, type: 'checkbox', items, checkedValues: checkedVals};
                    }
                    const hasValue = !this.isEmpty(ec && ec.result);
                    return {
                        key: path, type: type || 'value', hasValue,
                        label: (ec && (ec.text || ec.title)) || null,
                        displayValue: this.shortVal(ec && ec.result),
                        claim: hasValue ? track(this.claimByPath(path)) : null,
                    };
                });
                return {
                    key: `opt-${i}`, text: opt && opt.text, value, selected,
                    claim: selected ? track(this.rootOptionClaim(value)) : null,
                    elements,
                };
            });

            const freeClaims = this.serverClaims
                .filter((c) => c.struct_path === 'ROOT' && c.kind !== 'option_selected')
                .map((c, idx) => {
                    rendered.add(c.claim_id);
                    return {
                        key: `free-${idx}`, displayValue: this.shortVal(c.value),
                        claim: this.attachVerdict(c.claim_id),
                    };
                });

            // 좌측 트리에 조인되지 못한 서버 주장(중첩 라디오/표 등 미래 타입 대비) —
            // 우측 주장 카드에는 나오지만 왼쪽에서 누락되지 않도록 fallback 으로 표시.
            const unmatchedClaims = this.serverClaims
                .filter((c) => !rendered.has(c.claim_id))
                .map((c, idx) => ({
                    key: `unmatched-${idx}`,
                    label: c.label || c.path || null,
                    displayValue: this.shortVal(c.value),
                    claim: this.attachVerdict(c.claim_id),
                }));

            const anySelected = options.some((o) => o.selected)
                || freeClaims.length > 0 || unmatchedClaims.length > 0;
            // 상위 답변이 다중선택(checkbox)인지 단일선택(radio)인지 — 렌더 컨트롤 결정
            const multi = Array.isArray(snap.result);
            return {
                isNa, naReason, options, freeClaims, unmatchedClaims,
                multi,
                selectedValue: multi ? null : (resultVals.length ? resultVals[0] : null),
                selectedValues: resultVals,
                hasOptions: structure.length > 0,
                empty: !isNa && !anySelected,
            };
        },
    },
    watch: {
        deadlineDate(v) {
            // 날짜를 비우면(자동 기한으로) 시간도 버린다 — 날짜 없는 시간은 저장되지 않는다
            if (!v) this.deadlineTime = '';
            this.syncDeadlineToDraft();
        },
        deadlineTime() {
            this.syncDeadlineToDraft();
        },
        // 보기 상태 → URL (syncViewQuery)
        'job.job_id': 'syncViewQuery',
        activeTab: 'syncViewQuery',
        activeLocale: 'syncViewQuery',
        itemFilter: {handler: 'syncViewQuery', deep: true},
        itemSort: 'syncViewQuery',
        itemView: 'syncViewQuery',
        detailOpen: 'syncViewQuery',
    },
    created() {
        this.loadLatestJob();
        this.loadMeta();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    },
    beforeDestroy() {
        this.stopPolling();
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        // 저장하지 않은 문항 수정(상세 모달)·메일 초안 편집이 있으면 라우트 이탈 전 확인
        const what = [this.itemDirty && '문항 수정', this.isDraftDirty && '메일 초안 편집'].filter(Boolean);
        if (!what.length) { next(); return; }
        this.$showModal({
            type: 'confirm', state: 'warning', title: '저장하지 않은 편집',
            message: `저장하지 않은 ${what.join('과 ')}이 있습니다. 이 화면을 벗어나면 편집 내용이 사라집니다. 나가시겠습니까?`,
        }).then((ok) => next(ok ? undefined : false));
    },
    methods: {
        // ── 조회/폴링 ────────────────────────────────────────────────
        // 헤더용 이름 조회 — 캠페인 보고서와 동일한 /assessments/results 로스터(SSoT=nice)에서
        // company_id 로 매칭. campaign_id/company_id 만으로는 이름을 알 수 없기 때문.
        async loadMeta() {
            if (!this.campaignId || !this.companyId) return;
            try {
                const {data} = await this.$axios.get('/assessments/results',
                    {params: {campaign_id: this.campaignId}});
                const rows = Array.isArray(data) ? data : [];
                // 대체평가(priority)면 company_id 가 priority 쪽에 있을 수 있다
                const row = rows.find((r) => r.company_id === this.companyId
                    || (r.priority && r.priority.company_id === this.companyId));
                if (!row) return;
                const pick = (k) => (row.priority && row.priority[k] != null
                    ? row.priority[k] : row[k]);
                this.meta = {
                    projectCode: pick('project_code'),
                    companyName: pick('company_name'),
                    campaignName: pick('campaign_name'),
                    period: pick('period') || null,  // 로스터에 있으면 '← 목록' 복원에 쓴다
                };
                // 초안이 meta 보다 먼저 로드됐고 미저장 편집이 없으면 기준선을 다시 잡아 알약에
                // 업체/캠페인명을 반영한다(serializeDraftFields 는 meta 를 안 보므로 dirty 기준선 불변).
                // 편집 중이면 건드리지 않는다 — 다음 [저장 + 미리보기] 가 반영한다.
                // 초안이 아직 없으면 이후 loadDraftSilently/createDraft 의 markDraftSaved 가 meta 를 본다.
                if (this.draft && !this.isDraftDirty) this.markDraftSaved();
            } catch (e) {
                // 이름 조회 실패해도 헤더는 ID 폴백으로 계속 동작
            }
        },
        goBack() {
            // 목록에서 진입했으면 뒤로가기로 필터/페이지 상태 복원. 새 탭(캠페인 보고서)·딥링크는
            // history 직전 항목이 목록이 아니므로 이 업체의 프로젝트를 고른 목록으로 보낸다.
            if (this.cameFromList) {
                this.$router.back();
                return;
            }
            const query = {};
            if (this.meta.period) query.period = this.meta.period;
            if (this.meta.projectCode) query.project_code = this.meta.projectCode;
            this.$router.push({name: 'ManagingSupplementReviewList', query}).catch(() => {});
        },
        // ── 문항 단일 재판정(rerun) ─────────────────────────────────
        isReruning(item) {
            return this.reruningIds.includes(item.item_id);
        },
        // 처리 실패(종결) 문항 하나만 다시 판정. 백엔드가 잡을 RUNNING 으로
        // 되돌리므로(202), loadJob 으로 재로딩하면 기존 폴링이 재개돼
        // PENDING→판정완료가 자동 반영된다. 활성 잡이면 백엔드가 409.
        async rerunItem(item) {
            if (!this.job || this.reruningIds.includes(item.item_id)) return;
            this.reruningIds.push(item.item_id);
            try {
                await this.$axios.post(
                    `/supplement-review/${this.job.job_id}/items/${item.item_id}/rerun`);
                this.$EventBus.$emit('toast-success', '재판정 시작', `${item.sub_code} 문항을 다시 판정합니다.`);
                await this.reloadJobAfterWrite('재판정 요청은 접수됐습니다');
            } catch (e) {
                if (e.response && e.response.status === 401) return;
                this.$EventBus.$emit('toast-failure', '재판정 실패',
                    getErrorMessage(e, '문항 재판정 요청에 실패했습니다.'));
            } finally {
                this.reruningIds = this.reruningIds.filter((id) => id !== item.item_id);
            }
        },
        // 처리 실패(FAILED) 문항 전체를 한 번에 재판정. 백엔드 bulk-rerun 은 잡을
        // 한 번만 RUNNING 으로 전이하고 각 문항을 병렬 처리하므로, 단건 rerun 을
        // 반복하다 잡이 RUNNING 이 돼 나머지 재판정 버튼이 사라지던 문제를 없앤다.
        async rerunAllFailed() {
            if (!this.job || this.reruningAll) return;
            const ids = this.failedItems.map((i) => i.item_id);
            if (!ids.length) return;
            const name = (this.meta && this.meta.companyName) || `업체 ${this.job.company_id}`;
            const ok = await this.$showModal({type: 'confirm', state: 'warning', title: '실패 문항 재판정',
                message: `${name} 의 처리 실패 문항 ${ids.length}건을 AI 로 다시 판정합니다(AI 비용 발생). 완료될 때까지 보완요청 메일 초안 탭을 사용할 수 없습니다.`});
            if (!ok) return;
            this.reruningAll = true;
            try {
                const {data} = await this.$axios.post(
                    `/supplement-review/${this.job.job_id}/items/bulk-rerun`,
                    {item_ids: ids});
                const n = (data.dispatched || []).length;
                const skipped = (data.skipped || []).length;
                const failed = (data.failed || []).length;
                let msg = `${n}건 재판정을 시작했습니다.`;
                if (skipped) msg += ` (${skipped}건 제외)`;
                if (failed) msg += ` (${failed}건 발행 실패 — 잠시 후 자동 회수)`;
                // 접수 결과는 재조회 성공과 무관하게 먼저 알린다(재조회 실패를 '재판정 실패'로 보이지 않게)
                this.$EventBus.$emit('toast-success', '재판정 시작', msg);
                await this.reloadJobAfterWrite('재판정 요청은 접수됐습니다');
            } catch (e) {
                if (e.response && e.response.status === 401) return;
                this.$EventBus.$emit('toast-failure', '재판정 실패',
                    getErrorMessage(e, '실패 문항 재판정 요청에 실패했습니다.'));
            } finally {
                this.reruningAll = false;
            }
        },
        async loadLatestJob() {
            // 업체를 모르는 링크 — 필터 없는 jobs 조회는 전체 최신 잡(다른 업체)을 돌려준다
            if (!this.campaignId || !this.companyId) {
                this.invalidLink = true;
                this.jobLoading = false;
                return;
            }
            const jobIdFromQuery = Number(queryStr(this.$route.query, 'job_id')) || null;
            this.jobLoading = true;
            this.loadError = false;
            try {
                let job = null;
                if (jobIdFromQuery) {
                    ({data: job} = await this.$axios.get(`/supplement-review/${jobIdFromQuery}`));
                    // 손으로 고친 링크 등으로 다른 업체 잡이면 쓰지 않는다(엉뚱한 업체에 메일·요청 방지)
                    if (job.campaign_id !== this.campaignId || job.company_id !== this.companyId) {
                        job = null;
                        this.$EventBus.$emit('toast-warning', '다른 업체의 보완검토',
                            '링크의 보완검토 회차가 이 업체 것이 아니어서 이 업체의 최신 회차를 엽니다.');
                    }
                }
                if (!job) {
                    const {data} = await this.$axios.get('/supplement-review/jobs', {
                        params: {campaign_id: this.campaignId, company_id: this.companyId},
                    });
                    if (data.length > 0) {
                        ({data: job} = await this.$axios.get(`/supplement-review/${data[0].job_id}`));
                    }
                }
                if (job) this.applyJob(job);
                this.openPendingItem();
            } catch (e) {
                if (e.response && e.response.status === 401) return;
                this.loadError = true;
                this.$EventBus.$emit('toast-failure', '조회 실패', '보완검토 이력을 불러오지 못했습니다.');
            } finally {
                this.jobLoading = false;
            }
        },
        async loadJob(jobId) {
            // 마지막 요청 응답만 반영 — 폴링 중 새 잡으로 바꾼 뒤 늦게 온 이전 잡 응답이 되돌리지 않도록
            const seq = ++this.jobReqSeq;
            const {data} = await this.$axios.get(`/supplement-review/${jobId}`);
            if (seq === this.jobReqSeq) this.applyJob(data);
        },
        applyJob(data) {
            // 다른 잡(새 요청·409 재사용)으로 바뀌면 이전 잡의 초안·문항 상세를 들고 있지 않는다.
            // 이전 잡에 대해 날아가 있는 초안·상세 응답은 잡 ID·순번 검사로 버려진다.
            if (this.job && this.job.job_id !== data.job_id) {
                this.draft = null;
                this.draftLoadError = false;
                this.recipientsExpanded = false;
                this.activeLocale = 'ko';
                this.detailReqSeq += 1;
                this.openingItemId = null;
            }
            this.pollFailCount = 0;
            this.pollError = false;
            this.job = data;
            this.items = data.items || [];
            // 메일 초안 탭은 완료 잡에서만 열린다 — ?tab=draft 로 진행 중 잡을 열면 문항 판정 탭으로
            if (!this.isJobCompleted) this.activeTab = 0;
            if (this.isJobActive) {
                this.startPolling();
            } else {
                this.stopPolling();
                // 저장하지 않은 초안 편집이 있으면 재로드로 덮어쓰지 않는다.
                // 문항 저장(saveItem→loadJob) 등으로 loadJob 이 재호출될 때 dirty
                // 초안을 조용히 날리고 이탈/복사 가드까지 리셋되는 것을 막는다.
                if (!this.isDraftDirty) this.loadDraftSilently();
            }
        },
        startPolling() {
            this.stopPolling();
            this.pollTimer = setInterval(async () => {
                try {
                    await this.loadJob(this.job.job_id);
                } catch (e) {
                    // 일시 실패는 다음 틱에서 재시도, 연속 3회 실패해야 중단하고 알린다
                    this.pollFailCount += 1;
                    if (this.pollFailCount >= 3) {
                        this.stopPolling();
                        this.pollError = true;
                    }
                }
            }, 4000);
        },
        async refreshJob() {
            this.pollError = false;
            this.pollFailCount = 0;
            try {
                await this.loadJob(this.job.job_id);
            } catch (e) {
                this.pollError = true;
                this.$EventBus.$emit('toast-failure', '조회 실패', '진행 상황을 불러오지 못했습니다.');
            }
        },
        // 쓰기(저장·재판정)는 이미 성공한 뒤의 재조회 — 실패해도 '실패'로 보이지 않게 안내만 하고
        // [지금 새로고침](pollError)을 띄운다. 예외를 밖으로 던지지 않는다.
        async reloadJobAfterWrite(doneTitle) {
            try {
                await this.loadJob(this.job.job_id);
            } catch (e) {
                if (e.response && e.response.status === 401) return;
                this.stopPolling();
                this.pollError = true;
                this.$EventBus.$emit('toast-warning', doneTitle,
                    '최신 목록을 불러오지 못했습니다. [지금 새로고침]을 눌러 주세요.');
            }
        },
        // 보기 상태(잡·탭·필터·정렬·초안 언어·열린 문항)를 URL 에 남긴다 — 새로고침·링크 공유용.
        // 기본값은 키를 뺀다. push 하지 않는 이유: '← 목록'이 history.back() 이라 보기 전환이
        // 기록에 쌓이면 목록으로 돌아가지 못한다(모두 같은 화면 안의 상태라 replace).
        syncViewQuery() {
            const q = {...this.$route.query};
            const set = (key, value) => {
                if (value) q[key] = String(value); else delete q[key];
            };
            set('job_id', this.job && this.job.job_id);
            set('tab', this.activeTab === 1 && 'draft');
            set('original', this.itemFilter.original !== 'ALL' && this.itemFilter.original);
            set('current', this.itemFilter.current !== 'ALL' && this.itemFilter.current);
            set('sort', this.itemSort.key);
            set('dir', this.itemSort.key && this.itemSort.dir === -1 && 'desc');
            set('view', this.itemView === 'list' && 'list');
            set('draft_lang', this.activeLocale === 'en' && 'en');
            set('item', this.detailOpen && this.detail ? this.detail.item_id : this.pendingItemId);
            const cur = this.$route.query;
            const same = Object.keys(q).length === Object.keys(cur).length
                && Object.keys(q).every((k) => q[k] === cur[k]);
            if (!same) this.$router.replace({query: q}).catch(() => {});
        },
        // ?item= 링크의 문항을 한 번 연다(이 잡에 없는 문항이면 키만 지운다)
        openPendingItem() {
            const id = this.pendingItemId;
            if (!id) return;
            this.pendingItemId = null;
            const item = this.items.find((it) => it.item_id === id);
            if (item) this.openDetail(item); else this.syncViewQuery();
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        // ── 잡 생성 ──────────────────────────────────────────────────
        async createJob() {
            if (!this.campaignId || !this.companyId) {
                this.$showModal({type: 'alert', state: 'warn', title: '파라미터 없음',
                    message: 'campaign_id / company_id 가 필요합니다. 캠페인 보고서 화면에서 진입하세요.'});
                return;
            }
            // 저장하지 않은 초안 편집이 있으면 새 잡 생성이 이를 날린다 — 확인
            if (this.isDraftDirty) {
                const ok = await this.$showModal({
                    type: 'confirm', state: 'warning', title: '저장하지 않은 편집',
                    message: '저장하지 않은 메일 초안 편집이 있습니다. 새 보완검토를 요청하면 편집 내용이 사라집니다. 계속하시겠습니까?',
                });
                if (!ok) return;
            }
            // 기존 잡이 있으면 새 잡이 현재 결과를 대체한다 — 비용 발생 확인
            if (this.job) {
                const ok = await this.$showModal({
                    type: 'confirm', state: 'warning', title: '새 보완검토 요청',
                    message: `새로 요청하면 AI 비용이 발생하고, 현재 결과(Job #${this.job.job_id}) 대신 새 결과가 표시됩니다. 이 화면에서는 이전 결과로 돌아갈 수 없습니다. 계속하시겠습니까?`,
                });
                if (!ok) return;
            }
            this.creating = true;
            try {
                const {data} = await this.$axios.post('/supplement-review', {
                    campaign_id: this.campaignId, company_id: this.companyId,
                });
                this.draft = null;
                this.recipientsExpanded = false;
                this.activeLocale = 'ko';
                await this.loadJob(data.job_id);
            } catch (e) {
                const res = e.response || {};
                if (res.status === 409 && res.data && res.data.job_id) {
                    await this.loadJob(res.data.job_id);
                } else {
                    this.$showModal({type: 'alert', state: 'error', title: '요청 실패',
                        message: (res.data && res.data.message) || '보완검토 요청에 실패했습니다.'});
                }
            } finally {
                this.creating = false;
            }
        },
        // ── 상세/override ────────────────────────────────────────────
        async openDetail(item) {
            // 연속 클릭 시 늦게 온 이전 응답이 마지막 문항을 덮지 않도록 순번으로 거른다
            const seq = ++this.detailReqSeq;
            this.openingItemId = item.item_id;
            try {
                const {data} = await this.$axios.get(
                    `/supplement-review/${this.job.job_id}/items/${item.item_id}`);
                if (seq !== this.detailReqSeq) return;
                this.skipHideGuard = false;
                this.detail = data;
                this.editStatus = data.current_status;
                this.editReason = '';
                this.editInclude = data.include_in_email;
                this.editFeedback = data.feedback_text || '';
                this.reviewerChecked = !!data.reviewer_checked;
                this.itemChatInput = '';
                this.itemChatting = false;
                // 패널은 기본 닫힘 — 이 문항에 대화 이력이 있으면 자동으로 펼쳐 보여준다
                this.itemChatOpen = (data.turns || []).length > 0;
                this.detailOpen = true;
                // 펼쳐진 상태면 대화를 최신 턴으로 — 재시도 루프가 모달
                // 렌더/트랜지션이 끝날 때까지 기다렸다 적용한다
                if (this.itemChatOpen) this.scrollItemChat();
            } catch (e) {
                if (seq !== this.detailReqSeq || (e.response && e.response.status === 401)) return;
                this.$EventBus.$emit('toast-failure', '조회 실패', '문항 상세를 불러오지 못했습니다.');
            } finally {
                if (seq === this.detailReqSeq) this.openingItemId = null;
            }
        },
        // 바깥 클릭·ESC·닫기 버튼 — 저장하지 않은 수정이 있으면 확인 후 닫는다
        async onDetailHide(bvEvt) {
            if (this.skipHideGuard || !this.itemDirty) return;
            bvEvt.preventDefault();
            const ok = await this.$showModal({
                type: 'confirm', state: 'warning', title: '저장하지 않은 수정',
                message: '저장하지 않은 문항 수정이 있습니다. 닫으면 수정 내용이 사라집니다. 닫으시겠습니까?',
            });
            if (!ok) return;
            this.skipHideGuard = true;
            this.detailOpen = false;
        },
        async saveItem() {
            const body = {
                feedback_text: this.editFeedback,
                include_in_email: this.editInclude,
            };
            // 내부 피드백은 상태 변경 여부와 무관하게 항상 저장한다 — 예전에는
            // 상태를 바꿀 때만 전송돼 "판정 유지하며 남긴 지적"이 조용히 버려졌다.
            if (this.editReason.trim()) {
                body.internal_note = this.editReason.trim();
            }
            if (this.editStatus !== this.detail.current_status) {
                if (!this.editReason.trim()) {
                    this.$showModal({type: 'alert', state: 'warn', title: '피드백 필요',
                        message: '상태를 변경하려면 AI 개선용 피드백에 사유를 입력하세요.'});
                    return;
                }
                body.current_status = this.editStatus;
                body.reason = this.editReason.trim();
            }
            this.saving = true;
            try {
                await this.$axios.patch(
                    `/supplement-review/${this.job.job_id}/items/${this.detail.item_id}`, body);
                // PATCH 직후엔 detail 이 옛값이라 itemDirty 가 true — 닫기 가드를 건너뛴다
                this.skipHideGuard = true;
                this.detailOpen = false;
                this.$EventBus.$emit('toast-success', '저장했습니다', `${this.detail.sub_code} 문항을 저장했습니다.`);
                await this.reloadJobAfterWrite('저장은 완료됐습니다');
            } catch (e) {
                // 저장 실패는 모달을 열어 둔 채 알린다(입력을 지킨다)
                if (e.response && e.response.status === 401) return;
                this.$showModal({type: 'alert', state: 'error', title: '저장 실패',
                    message: getErrorMessage(e, '수정 사항 저장에 실패했습니다.')});
            } finally {
                this.saving = false;
            }
        },
        // ── 평가자 '확인함' 토글 ─────────────────────────────────────
        // saveItem 의 PATCH 와 섞지 않는다 — reviewer_checked 단독 요청이고
        // 백엔드도 판정/이력/has_override 와 무관하게 처리한다.
        async toggleReviewerChecked(checked) {
            const detail = this.detail;
            if (!detail || this.reviewerChecking) return;
            this.reviewerChecking = true;
            try {
                const {data} = await this.$axios.patch(
                    `/supplement-review/${this.job.job_id}/items/${detail.item_id}`,
                    {reviewer_checked: checked});
                // 응답(_item_to_dict)의 3필드를 신뢰, 없으면 로컬 계산으로 폴백
                const src = data && data.reviewer_checked !== undefined ? data : {
                    reviewer_checked: checked,
                    reviewer_checked_by: checked ? this.$store.state.user_id : null,
                    reviewer_checked_at: checked ? new Date().toISOString() : null,
                };
                const row = this.items.find((it) => it.item_id === detail.item_id);
                ['reviewer_checked', 'reviewer_checked_by', 'reviewer_checked_at'].forEach((k) => {
                    this.$set(detail, k, src[k]);
                    if (row) this.$set(row, k, src[k]);  // 목록 아이콘 즉시 반영
                });
            } catch (e) {
                this.reviewerChecked = !checked;  // 스위치 롤백
                const res = e.response || {};
                this.$EventBus.$emit('toast-failure', '확인 표시 실패',
                    (res.data && res.data.message) || '확인 표시를 저장하지 못했습니다.');
            } finally {
                this.reviewerChecking = false;
            }
        },
        reviewerCheckedLabel(item) {
            return `확인: ${this.formatSentAt(item.reviewer_checked_at)} (사용자 ${item.reviewer_checked_by})`;
        },
        // ── 문항별 대화 (상세 모달 우측 패널) ───────────────────────
        // 잡 단위 대화 탭/메서드는 제거됨 — 대화는 문항별 스레드로 일원화
        toggleItemChat() {
            this.itemChatOpen = !this.itemChatOpen;
            // 펼칠 때 chat-box 가 새로 마운트되어 scrollTop=0 이므로 최신 턴으로 이동
            if (this.itemChatOpen) this.scrollItemChat();
        },
        onItemChatEnter(e) {
            // 한글 IME 조합 중 Enter(keyCode 229)는 조합 확정용이다 — 이때 전송하면
            // v-model 에 마지막 글자가 아직 없고, 전송 후 입력창에 그 글자가 남는다.
            // 조합이 끝난 상태의 Enter 만 전송한다.
            if (e.isComposing || e.keyCode === 229) return;
            this.sendItemChat();
        },
        async sendItemChat() {
            const question = this.itemChatInput.trim();
            if (!question || this.itemChatting || !this.detail) return;
            // 전송 중 다른 문항으로 전환돼도 응답은 원래 문항 히스토리에 붙인다
            const detail = this.detail;
            if (!Array.isArray(detail.turns)) this.$set(detail, 'turns', []);
            this.itemChatting = true;
            this.itemChatInput = '';
            detail.turns.push({id: `local-${Date.now()}`, role: 'user', content: question});
            this.scrollItemChat();
            try {
                const {data} = await this.$axios.post(
                    `/supplement-review/${this.job.job_id}/chat`,
                    {question, item_id: detail.item_id});
                detail.turns.push({
                    id: data.answer.id, role: 'assistant', content: data.answer.content,
                });
            } catch (e) {
                const res = e.response || {};
                detail.turns.push({id: `err-${Date.now()}`, role: 'assistant',
                    content: (res.data && res.data.message) || '답변 생성에 실패했습니다.'});
            } finally {
                this.itemChatting = false;
                this.scrollItemChat();
            }
        },
        scrollItemChat() {
            // 패널이 b-modal 슬롯 안에 있어 두 가지 타이밍 함정이 있다:
            // (1) 펼침 직후 nextTick 에는 슬롯 재렌더가 안 끝나 ref 가 없거나
            //     아직 display:none(clientHeight 0)이다.
            // (2) 설정한 뒤에 지연된 슬롯 재렌더가 display none→flex 사이클을
            //     돌리면 브라우저가 scrollTop 을 0 으로 리셋한다.
            // → 보이는 상태가 될 때까지 기다렸다 설정하고, 다음 프레임에
            //   유지됐는지 확인해 풀렸으면 재적용한다. (패널이 닫혀 있으면
            //   재시도 소진 후 조용히 no-op)
            // setTimeout 사용 — rAF 는 창이 가려지면 멈춰 재시도가 실행되지 않는다
            const RETRY_MS = 32;
            const atBottom = (box) =>
                box.scrollTop >= box.scrollHeight - box.clientHeight - 4;
            const apply = (tries) => {
                const box = this.$refs.itemChatBox;
                if (box && box.clientHeight > 0) {
                    box.scrollTop = box.scrollHeight;
                    if (tries > 0) {
                        setTimeout(() => {
                            const b = this.$refs.itemChatBox;
                            if (b && b.clientHeight > 0 && !atBottom(b)) {
                                apply(tries - 1);
                            }
                        }, RETRY_MS);
                    }
                    return;
                }
                if (tries > 0) setTimeout(() => apply(tries - 1), RETRY_MS);
            };
            // 재시도 예산(~640ms)은 모달 페이드 트랜지션(~300ms)보다 길어야 한다
            this.$nextTick(() => apply(20));
        },
        // ── 메일 초안 ────────────────────────────────────────────────
        async loadDraftSilently() {
            // 응답이 오기 전에 다른 잡으로 바뀌었으면 버린다 — 이전 잡 초안이 '저장된 상태'로 되살아나
            // 새 잡에 저장·발송되는 것을 막는다(성공·실패 모두)
            const jobId = this.job.job_id;
            const stale = () => !this.job || this.job.job_id !== jobId;
            try {
                const {data} = await this.$axios.get(
                    `/supplement-review/${jobId}/email-draft`);
                if (stale()) return;
                this.draft = draftHelper.normalizeDraft(data);
                if (!this.draft.system_url) this.draft.system_url = draftHelper.DEFAULT_SYSTEM_URL;
                // ?draft_lang=en 링크라도 영문 초안이 없으면 국문 (빈 영문 편집기에 남지 않도록)
                if (!this.hasEn) this.activeLocale = 'ko';
                this.draftLoadError = false;
                this.recipientsExpanded = false;
                this.loadDeadlinePickers();
                this.markDraftSaved();
            } catch (e) {
                if (stale()) return;
                const status = e.response && e.response.status;
                if (status === 404) {  // 초안 미생성 — 자동 로드라 조용히 생성 버튼을 보인다
                    this.draftLoadError = false;
                    this.activeLocale = 'ko';
                    return;
                }
                if (status === 401) return;
                // 그 밖의 실패를 '미생성'으로 보이면 이미 만든 초안을 모르고 다시 만든다
                if (this.draft) {
                    this.$EventBus.$emit('toast-failure', '조회 실패', '최신 초안을 불러오지 못했습니다.');
                } else {
                    this.draftLoadError = true;
                }
            }
        },
        async createDraft(regenerate) {
            this.draftLoading = true;
            const jobId = this.job.job_id;
            try {
                const url = `/supplement-review/${jobId}/email-draft`
                    + (regenerate ? '?regenerate=true' : '');
                const {data} = await this.$axios.post(url);
                if (this.job.job_id !== jobId) return;  // 그사이 다른 잡으로 바뀜 — 이전 잡 초안을 얹지 않는다
                this.draft = draftHelper.normalizeDraft(data);
                if (!this.draft.system_url) this.draft.system_url = draftHelper.DEFAULT_SYSTEM_URL;
                this.recipientsExpanded = false;
                this.activeLocale = 'ko';  // (재)생성 초안은 영문 없음 — 빈 영문 화면에 남지 않도록
                this.loadDeadlinePickers();
                this.markDraftSaved();
            } catch (e) {
                const res = e.response || {};
                const err = (res.data && res.data.error) || '';
                // 409 template_missing — 시나리오 [보완요청]에 기본 템플릿이 없다. 등록 화면으로 안내한다.
                if (res.status === 409 && err === 'template_missing') {
                    const ok = await this.$showModal({
                        type: 'confirm', state: 'warning', title: '메일 템플릿 없음',
                        message: (res.data && res.data.message)
                            || '이메일 템플릿 관리 > 시나리오 [보완요청]에 기본 템플릿을 등록하세요.',
                        modalOptions: {okTitle: '템플릿 관리로 이동', cancelTitle: '닫기'},
                    });
                    if (ok) window.open(MAIL_TEMPLATE_URL, '_blank', 'noopener');
                    return;
                }
                this.$showModal({type: 'alert', state: 'error', title: '초안 생성 실패',
                    message: (res.data && res.data.message) || '초안 생성에 실패했습니다.'});
            } finally {
                this.draftLoading = false;
            }
        },
        confirmRegenerate() {
            this.$showModal({
                type: 'confirm', state: 'warning', title: '초안 다시 만들기',
                message: '초안을 현재 확정 상태 기준으로 처음부터 다시 만듭니다. '
                    + '수정한 제목·기한·URL·수신자·HTML 편집 내용이 모두 초기화되고 영문 번역본도 삭제됩니다. '
                    + '(수신자는 업체 담당자 기본값으로 다시 채워집니다.)',
            }).then((ok) => { if (ok) this.createDraft(true); });
        },
        async saveDraft() {
            // 모든 행을 제외한 채 저장하면 문항 표가 없는 메일이 된다 — 확인
            if (!this.draft.rows.length) {
                const ok = await this.$showModal({
                    type: 'confirm', state: 'warning', title: '표에 문항 없음',
                    message: '표에 포함된 문항이 하나도 없습니다. 이대로 저장하면 문항 표가 없는 메일이 됩니다. 계속하시겠습니까?',
                });
                if (!ok) return;
            }
            // 캔버스 편집 중이면 캔버스 디바운스(250ms)와 무관하게 현재 본문을 확정한다.
            const ed = this.$refs.bodyEditor;
            if (ed) {
                const v = ed.validateForSave();
                this.draft[this.bodyField] = v.bodyText;
                // 알약(변수)이 지워지거나 깨졌으면 업체에 변수가 빠진 메일이 나간다 — 확인
                if (!v.ok) {
                    const ok = await this.$showModal({
                        type: 'confirm', state: 'warning', title: '변수 손상',
                        message: '본문의 변수(업체명·기한·URL 등)가 사라졌거나 손상되었습니다. 그대로 저장하면 업체에 변수가 빠진 메일이 나갈 수 있습니다. 그래도 저장할까요?',
                    });
                    if (!ok) return;
                }
            }
            this.draftLoading = true;
            try {
                // 편집된 피드백에 맞춰 병합 키를 재계산한다. 이 과정을 빼면
                // 병합된(같은 문항 그룹·동일 문구) 비-첫 행의 편집이 메일 표에서
                // 사라진다(백엔드 렌더러가 첫 행 피드백으로 두 행을 덮어씀).
                // 백엔드도 PATCH 에서 재계산하지만 배포 순서와 무관하게 방어한다.
                this.draft.rows = draftHelper.normalizeRowKeys(this.draft.rows);
                const body = draftHelper.buildDraftPayload(this.draft);
                const jobId = this.job.job_id;
                const {data} = await this.$axios.patch(
                    `/supplement-review/${jobId}/email-draft`, body);
                if (this.job.job_id !== jobId) return;  // 그사이 다른 잡으로 바뀜 — 이전 잡 초안을 얹지 않는다
                this.draft = draftHelper.normalizeDraft(data);
                if (!this.draft.system_url) this.draft.system_url = draftHelper.DEFAULT_SYSTEM_URL;
                this.loadDeadlinePickers();
                this.markDraftSaved();
                this.previewRefreshKey += 1;
                this.$EventBus.$emit('toast-success', '저장했습니다', '메일 초안을 저장했습니다.');
            } catch (e) {
                const res = e.response || {};
                const errors = (res.data && res.data.errors) || [];
                this.$showModal({type: 'alert', state: 'error', title: '저장 실패',
                    message: (res.data && res.data.message || '초안 저장에 실패했습니다.')
                        + (errors.length ? `\n${errors.join('\n')}` : '')});
            } finally {
                this.draftLoading = false;
            }
        },
        insertSupplementVariable(variable) {
            // 비주얼 모드: 커서 위치에 값 알약(저장 시 토큰 원문으로 복원).
            const ed = this.$refs.bodyEditor;
            const key = variable.replace(/[{}\s]/g, '');
            if (ed && ed.insertVariable(variable, key)) return;
            // 소스 모드: textarea 선택 범위에 토큰 텍스트 삽입.
            const ta = this.$el.querySelector('.html-split-editor__textarea');
            const field = this.bodyField;  // 활성 로케일 본문에 삽입
            if (!ta) {
                this.draft[field] = (this.draft[field] || '') + variable;
                return;
            }
            const result = draftHelper.insertTextAtSelection(
                this.draft[field], ta.selectionStart, ta.selectionEnd, variable);
            this.draft[field] = result.text;
            this.$nextTick(() => {
                ta.selectionStart = ta.selectionEnd = result.caret;
                ta.focus();
            });
        },
        // 캔버스가 편집 불가 문서로 판정 — HtmlSplitEditor 가 이미 분할 모드로 강등했다.
        onBodyUnsupported() {
            this.$EventBus.$emit('toast-failure', '비주얼 편집 불가',
                '이 본문은 소스 모드로만 편집할 수 있습니다.');
        },
        // 날짜가 비면 명시 기한을 지워 서버의 자동 기한을, 시간만 비면 날짜만 저장한다.
        syncDeadlineToDraft() {
            if (!this.draft) return;
            this.draft.supplement_deadline = draftHelper.combineDeadline(
                this.deadlineDate, this.deadlineTime);
        },
        // 저장된 명시 기한만 복원, 없으면 비움(자동 기한은 서버가 계산·표시).
        // draft 에 되쓰지 않는다 — watch 가 사용자 입력 시에만 반영한다.
        loadDeadlinePickers() {
            if (!this.draft) return;
            const parts = draftHelper.resolveDeadlineParts(this.draft.supplement_deadline);
            this.deadlineDate = parts.date;
            this.deadlineTime = parts.time;
        },
        // 저장/로드 직후 현재 상태를 dirty 판정의 '깨끗함' 기준선으로 고정하고,
        // 본문 알약/미리보기용 변수 값도 이 시점 값으로 갱신한다.
        markDraftSaved() {
            this.savedSnapshot = this.draftSnapshot;
            this.appliedVariableValues = {
                ko: draftHelper.buildVariableValues(this.draft, this.meta, 'ko'),
                en: draftHelper.buildVariableValues(this.draft, this.meta, 'en'),
            };
        },
        // ── 국문/영문 전환 · AI 번역 · 영문 삭제 ──────────────────────
        // 전환 순서: 현재 본문 플러시 → activeLocale 변경 → :key 로 에디터 재마운트.
        // v-model 대신 :checked/@input 을 쓰는 이유 — 값이 먼저 바뀌면 bodyField 가
        // 새 로케일을 가리켜 이전 로케일 본문에 플러시할 수 없다.
        switchLocale(next) {
            if (!this.draft || next === this.activeLocale) return;
            const ed = this.$refs.bodyEditor;
            if (ed) this.draft[this.bodyField] = ed.getBodyText();
            this.activeLocale = next;
        },
        // 번역 없음 안내 모달 — 항목 줄 + 질문지 번역 바로가기 링크(있으면). $showModal 은
        // VNode message 를 받으므로 본문 아래 링크를 그대로 얹는다.
        showEnPlaceholderAlert(lines) {
            const h = this.$createElement;
            const body = [
                h('p', {class: 'mb-2'},
                    '다음 항목은 영문 번역 값이 없어 표에 \'번역 없음\'으로 대체했습니다.'),
                ...lines.map((line) => h('p', {class: 'mb-1 small'}, line)),
                h('p', {class: this.surveyTranslationUrl ? 'mb-2' : 'mb-0'},
                    '질문지 번역 화면에서 번역한 뒤 \'영문 다시 번역하기\'를 눌러주세요.'),
            ];
            if (this.surveyTranslationUrl) {
                body.push(h('a', {
                    attrs: {href: this.surveyTranslationUrl, target: '_blank', rel: 'noopener'},
                }, '질문지 번역 바로가기'));
            }
            this.$showModal({
                type: 'alert', state: 'warning',
                title: '영문 번역 완료 — 번역 없는 항목 있음',
                message: h('div', body),
            });
        },
        // 저장된 국문을 번역한다(서버 stateless) — 미저장 편집은 먼저 저장, 실패하면 중단.
        async translateDraft() {
            if (!this.draft || this.translating || this.draftLoading) return;
            if (this.hasEn) {
                const ok = await this.$showModal({
                    type: 'confirm', state: 'warning', title: '영문 다시 번역하기',
                    message: '영문 본문을 템플릿으로 다시 만들고 피드백만 새로 번역합니다. 직접 수정한 영문은 사라집니다.',
                });
                if (!ok) return;
            }
            if (this.isDraftDirty) {
                await this.saveDraft();
                if (this.isDraftDirty) return;  // 저장 취소/실패 → 번역 중단
            }
            this.translating = true;
            const jobId = this.job.job_id;
            try {
                const {data} = await this.$axios.post(
                    `/supplement-review/${jobId}/email-draft/translate`);
                if (this.job.job_id !== jobId) return;  // 그사이 다른 잡으로 바뀜 — 이전 잡 초안을 얹지 않는다
                this.draft = draftHelper.normalizeDraft(data);
                if (!this.draft.system_url) this.draft.system_url = draftHelper.DEFAULT_SYSTEM_URL;
                this.loadDeadlinePickers();
                this.markDraftSaved();
                this.activeLocale = 'en';
                this.previewRefreshKey += 1;
                // 번역 없는 항목이 있으면 toast 대신 모달로 확실히 알린다(표에는 '번역 없음' 대체됨).
                // 모달을 띄울지는 서버 en_placeholders.total 로 판단(없으면 dedup total 폴백).
                // 표시 lines 는 rows 로 대분류를 이름 dedup 한 결과 — 표의 '번역 없음' 칸 수와 맞춘다.
                const placeholders = (data && data.en_placeholders) || {};
                const {total: sumTotal, lines} = draftHelper.summarizeEnMissing(
                    placeholders, this.draft.rows);
                const total = placeholders.total != null ? placeholders.total : sumTotal;
                if (total > 0) {
                    this.showEnPlaceholderAlert(lines);
                } else {
                    this.$EventBus.$emit('toast-success', '영문 번역 완료', '영문 제목·본문을 만들었습니다.');
                }
            } catch (e) {
                const res = e.response || {};
                this.$showModal({type: 'alert', state: 'error', title: '번역 실패',
                    message: (res.data && res.data.message) || '영문 번역에 실패했습니다.'});
            } finally {
                this.translating = false;
            }
        },
        // 영문 제목/본문을 비우고 저장 → 서버가 NULL 로 정규화(영문 없음 상태로 복귀)
        confirmDeleteEn() {
            this.$showModal({
                type: 'confirm', state: 'warning', title: '영문 삭제',
                message: '영문 제목·본문을 삭제합니다. 이후 발송은 국문만 가능합니다. 계속하시겠습니까?',
            }).then(async (ok) => {
                if (!ok) return;
                // 먼저 국문으로 전환하고 재마운트를 기다린다 — 같은 틱에 saveDraft 를 부르면
                // 아직 붙어 있는 영문 에디터가 플러시되어 삭제한 영문이 되살아나거나 국문을 덮는다.
                this.activeLocale = 'ko';
                await this.$nextTick();
                this.draft.subject_en = '';
                this.draft.body_html_en = '';
                this.saveDraft();
            });
        },
        onBeforeUnload(e) {
            // 새로고침/창 닫기 시 미저장 편집(초안·문항 상세) 경고 (브라우저 기본 확인창)
            if (!this.isDraftDirty && !this.itemDirty) return;
            e.preventDefault();
            e.returnValue = '';
        },
        // ── 수신자 목록 (발송 대상) ──────────────────────────────────
        recipientRoleLabel(rcpt) {
            if (rcpt && rcpt.role === 2) return '관리자';
            if (rcpt && rcpt.role === 1) return '일반 참여자';
            return '수동 추가';
        },
        recipientRoleVariant(rcpt) {
            if (rcpt && rcpt.role === 2) return 'primary';
            if (rcpt && rcpt.role === 1) return 'info';
            return 'secondary';
        },
        addRecipient() {
            if (!this.draft) return;
            // 형식 오류·중복은 입력을 남기고 이유를 보인다(지우면 추가된 줄 안다)
            const err = draftHelper.validateManualRecipient(this.draft.recipients, this.newRecipientEmail);
            this.recipientError = RECIPIENT_ERRORS[err] || '';
            if (err) return;
            const wasEmpty = !(this.draft.recipients || []).length;
            const next = draftHelper.addManualRecipient(this.draft.recipients, this.newRecipientEmail);
            this.$set(this.draft, 'recipients', next);
            // 빈 상태에서 첫 수신자를 넣은 직후 입력란이 사라지지 않도록 펼침으로 전환
            if (wasEmpty && next.length) this.recipientsExpanded = true;
            this.newRecipientEmail = '';
        },
        removeRecipient(idx) {
            if (!this.draft || !Array.isArray(this.draft.recipients)) return;
            this.draft.recipients.splice(idx, 1);
        },
        formatSentAt(ts) {
            if (!ts) return '';
            const d = new Date(ts);
            return Number.isNaN(d.getTime()) ? ts : d.toLocaleString('ko-KR');
        },
        // 즉시 발송 — 저장(비-dirty) + 수신자 존재가 전제. 발송 이력은 백엔드가 남긴다.
        async sendDraft() {
            if (this.isDraftDirty) {
                this.$showModal({type: 'alert', state: 'warning', title: '저장 필요',
                    message: '저장하지 않은 편집이 있습니다. 먼저 [저장 + 미리보기]를 눌러 주세요.'});
                return;
            }
            const rcpts = (this.draft && this.draft.recipients) || [];
            if (!rcpts.length) {
                this.$showModal({type: 'alert', state: 'warning', title: '수신자 없음',
                    message: '수신자가 없습니다. 수신자를 추가해 주세요.'});
                return;
            }
            if (this.enSendBlockedNow) return;  // 버튼 disabled 와 동일 게이트
            // 체크박스가 필요해 confirm 은 로컬 모달(§13). 열 때마다 재오픈=on, override=off 로 리셋(§5).
            const init = draftHelper.openSendConfirmState();
            this.reopenAnswers = init.reopenAnswers;
            this.allowPlaceholders = init.allowPlaceholders;
            this.sendConfirmOpen = true;
        },
        // 발송 confirm 의 [지금 발송] — 실제 POST
        async doSendDraft() {
            if (!this.draft || !this.job || this.sending) return;
            const rcpts = this.draft.recipients || [];
            if (!rcpts.length || this.isDraftDirty || this.enSendBlockedNow) return;
            // 발송 언어 = 현재 로케일(§11) — 라디오 없이 confirm 에 언어만 명시
            const locale = this.effectiveLocale;
            this.sending = true;
            this.draftLoading = true;
            const jobId = this.job.job_id;
            try {
                const {data} = await this.$axios.post(
                    `/supplement-review/${jobId}/email-draft/send`,
                    draftHelper.buildSendPayload(this.draft, {
                        locale,
                        reopenAnswers: this.reopenAnswers,
                        allowPlaceholders: this.allowPlaceholders,
                    }));
                const sentAt = (data && data.sent_at) || new Date().toISOString();
                // 발송 안내는 그대로 하되, 그사이 다른 잡으로 바뀌었으면 그 잡 초안에 발송 시각을 찍지 않는다
                if (this.draft && this.job.job_id === jobId) this.$set(this.draft, 'last_sent_at', sentAt);
                const n = (data && data.sent_count != null) ? data.sent_count : rcpts.length;
                const detail = `${n}명에게 ${locale === 'en' ? '영문' : '국문'}으로 발송했습니다.`;
                // §13 상태 전환 결과별 안내(모두 토스트). reopen 이 없으면 구 BE — 발송 결과만.
                const reopen = data && data.reopen;
                if (!reopen) {
                    this.$EventBus.$emit('toast-success', '발송 완료', detail);
                } else if (reopen.done) {
                    this.$EventBus.$emit('toast-success', '발송 완료 · 답변 가능 상태로 전환됨', detail);
                } else if (reopen.skipped_reason) {
                    const why = draftHelper.REOPEN_SKIP_LABEL[reopen.skipped_reason]
                        || `상태 전환을 건너뛰었습니다(${reopen.skipped_reason}).`;
                    this.$EventBus.$emit('toast-success', '발송 완료 · 상태 전환 생략', `${detail} ${why}`);
                } else {
                    // error 또는 사유 불명 — 메일은 이미 나갔으므로 수동 전환을 안내
                    this.$EventBus.$emit('toast-failure',
                        '발송 완료 · 상태 전환 실패 — 평가관리에서 직접 전환해 주세요', detail);
                }
            } catch (e) {
                const res = e.response || {};
                // 409 분기(en_placeholders_present / draft_stale)와 부작용은 헬퍼가 결정,
                // 부작용 실행은 hooks 로 주입(§5).
                draftHelper.handleSendError(
                    {status: res.status,
                        error: res.data && res.data.error,
                        message: res.data && res.data.message},
                    {
                        showModal: (opts) => this.$showModal(opts),
                        closeConfirm: () => { this.sendConfirmOpen = false; },
                        reloadDraft: () => this.loadDraftSilently(),
                    });
            } finally {
                this.sending = false;
                this.draftLoading = false;
            }
        },
        // 발송 테스트 — 백엔드가 현재 로그인 사용자 이메일로만 보낸다(수신자 목록 무시,
        // 제목 "[테스트] " 접두, last_sent_at 미갱신). sendDraft 와 독립.
        async sendTestDraft() {
            if (this.testSending || this.isDraftDirty || this.enSendBlockedNow) return;
            const email = this.$store.getters.getEMail;
            const who = email ? `현재 로그인한 계정(${email})` : '현재 로그인 계정';
            const locale = this.effectiveLocale;  // 실발송과 동일 규칙(현재 로케일)
            const baseText = `${who}으로 테스트 메일을 보냅니다. ${this.localeNotice} 실제 수신자에게는 발송되지 않습니다.`;
            const h = this.$createElement;
            const warn = this.enMissingWarning;  // 영문 '번역 없음' 경고(빨강 한 줄) — 발송은 막지 않음
            const ok = await this.$showModal({
                type: 'confirm', state: 'warning', title: '발송 테스트',
                message: warn
                    ? h('div', [h('p', {class: 'mb-2'}, baseText), h('p', {class: 'text-danger mb-0'}, [h('AppIcon', {props: {name: 'alert-triangle'}, class: 'mr-1'}), warn])])
                    : baseText,
            });
            if (!ok) return;
            this.testSending = true;
            try {
                const {data} = await this.$axios.post(
                    `/supplement-review/${this.job.job_id}/email-draft/send-test`,
                    draftHelper.buildTestSendPayload(this.draft, {locale}));
                this.$EventBus.$emit('toast-success', '발송 테스트',
                    `테스트 메일 발송: ${(data && data.sent_to) || email || ''}`);
            } catch (e) {
                const res = e.response || {};
                this.$EventBus.$emit('toast-failure', '발송 테스트 실패',
                    (res.data && res.data.message) || '테스트 메일 발송에 실패했습니다.');
            } finally {
                this.testSending = false;
            }
        },
        // ── 주장-증빙 상세 헬퍼 ──────────────────────────────────────
        asArray(v) {
            if (v == null) return [];
            if (Array.isArray(v)) return v;
            if (typeof v === 'string' && v.trim() === '') return [];
            return [v];
        },
        isEmpty(v) {
            if (v == null) return true;
            if (typeof v === 'string') return v.trim() === '';
            if (Array.isArray(v)) return v.length === 0;
            if (typeof v === 'object') return Object.keys(v).length === 0;
            return false;
        },
        shortVal(v, n = 200) {
            const s = typeof v === 'string' ? v : JSON.stringify(v);
            if (!s) return '';
            return s.length > n ? `${s.slice(0, n)}…` : s;
        },
        attachVerdict(claimId) {
            const v = claimId != null ? this.verdictByClaimId[claimId] : null;
            return {claim_id: claimId, cj: v ? v.cj : null, group: v ? v.group : null};
        },
        rootOptionClaim(value) {
            const c = this.serverClaims.find(
                (sc) => sc.struct_path === 'ROOT'
                    && sc.kind === 'option_selected' && sc.value === value);
            return c ? this.attachVerdict(c.claim_id) : null;
        },
        claimByPath(path) {
            const c = this.serverClaims.find((sc) => sc.struct_path === path);
            return c ? this.attachVerdict(c.claim_id) : null;
        },
        // 노드 하이라이트 클래스 — 판정 verdict + hover 연동
        nodeClass(claim) {
            const classes = [];
            const cj = claim && claim.cj;
            if (cj) {
                if (cj.needs_evidence === false) classes.push('sr-node-exempt');
                else if (cj.is_supported === true) classes.push('sr-node-supported');
                else if (cj.is_supported === false) classes.push('sr-node-unsupported');
                else classes.push('sr-node-review');
            }
            if (claim && claim.group != null && claim.group === this.hoverGroup) {
                classes.push('sr-node-active');
            }
            return classes.join(' ');
        },
        verdictIcon(claim) {
            const cj = claim && claim.cj;
            if (!cj) return 'circle';
            if (cj.needs_evidence === false) return 'minus';
            if (cj.is_supported === true) return 'check-circle';
            if (cj.is_supported === false) return 'exclamation-circle';
            return 'question-circle';
        },
        verdictTextClass(claim) {
            const cj = claim && claim.cj;
            if (!cj) return 'text-muted';
            if (cj.needs_evidence === false) return 'text-secondary';
            if (cj.is_supported === true) return 'text-success';
            if (cj.is_supported === false) return 'text-danger';
            return 'text-warning';
        },
        cardClass(cj) {
            if (cj.needs_evidence === false) return 'sr-claim-exempt';
            if (cj.is_supported === true) return 'sr-claim-supported';
            if (cj.is_supported === false) return 'sr-claim-unsupported';
            return 'sr-claim-review';
        },
        cjBadge(cj) {
            if (cj.needs_evidence === false) return {variant: 'secondary', text: '증빙 불요'};
            if (cj.is_supported === true) return {variant: 'success', text: '증빙 확인'};
            if (cj.is_supported === false) return {variant: 'danger', text: '증빙 부족'};
            return {variant: 'warning', text: '확인 필요'};
        },
        // ── 첨부 다운로드 (일반 answer-attachment 다운로드 재사용) ────
        async downloadAttachment(att) {
            // 판정 시점 스냅샷의 storage_id 로 원본 파일을 블롭 다운로드한다.
            // 백엔드: GET /attachment/download (storage_id 는 배열, blob 응답).
            if (!att || att.storage_id == null || this.downloadingId != null) return;
            this.downloadingId = att.storage_id;
            try {
                const res = await this.$axios.get('/attachment/download', {
                    params: {
                        campaign_id: this.campaignId,
                        company_id: this.companyId,
                        storage_id: [att.storage_id],
                    },
                    responseType: 'blob',
                });
                // 공급업체가 올린 첨부(HTML/SVG 등 액티브 콘텐츠일 수 있음)를 관리자에게
                // 되돌려주는 경로다. 서버 content-type 을 그대로 Blob 에 실으면, a.download
                // 를 무시하는 레거시 브라우저(browserslist 에 ie>=11 선언)에서 클릭이 blob:
                // 로 네비게이션되어 동일 출처로 스크립트가 실행되고 localStorage 의 JWT 가
                // 탈취될 수 있다. 따라서 항상 application/octet-stream 으로 고정(렌더 대신
                // 저장)하고, download 속성 미지원 클라이언트는 msSaveOrOpenBlob 로 강제 저장한다.
                const filename = att.filename || `attachment_${att.storage_id}`;
                const blob = new Blob([res.data], {type: 'application/octet-stream'});
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob, filename);
                } else {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }
            } catch (e) {
                this.$showModal({type: 'alert', state: 'error', title: '다운로드 실패',
                    message: '첨부파일을 다운로드하지 못했습니다.'});
            } finally {
                this.downloadingId = null;
            }
        },
        // ── 표시 헬퍼 ────────────────────────────────────────────────
        includedInEmail(item) {
            return draftHelper.isIncludedInEmail(item);
        },
        jobStatusLabel(status) {
            return draftHelper.jobStatusLabel(status);
        },
        jobErrorLabel(code) {
            return draftHelper.jobErrorLabel(code);
        },
        confidenceLabel(c) {
            return draftHelper.CONFIDENCE_LABEL[c] || `확신도 ${c}`;
        },
        statusLabel(status) {
            return {
                NO_SUPPLEMENT: '보완 불필요',
                SUPPLEMENT_REQUIRED: '보완 필요',
                REVIEW_REQUIRED: '평가자 확인',
                PROCESSING_FAILED: '처리 실패',
            }[status] || status || '-';
        },
        // LLM 자유 텍스트(근거/사유)의 "storage_id 12345" 표기를 판정시점
        // 스냅샷의 파일명으로 치환 — 스냅샷 밖의 id/비정형 표기는 원문 유지.
        // 인용 무결성 키는 여전히 cited_storage_ids (표시 계층만 변경).
        withFileNames(text) {
            if (!text) return text;
            return String(text).replace(
                /storage[\s_-]?id\s*[:#=]?\s*(\d+)/gi,
                (match, id) => this.attachmentNameById[Number(id)] || match,
            );
        },
        // 판정 근거 뱃지 — 구조 규칙(사유 문구 매핑) vs AI 증빙 분석 구분.
        // 어느 쪽도 아니면(보완 불필요/판정 전) null → '-' 표시.
        verdictBasis(item) {
            const reason = item.supplement_reason || '';
            const hit = reason
                && VERDICT_BASIS_RULES.find((r) => reason.includes(r.m));
            if (hit) return {text: hit.text, reason};
            if (item.processing_status === 'FAILED') {
                return {text: '처리 실패', reason: item.error_code || ''};
            }
            if (['SUPPLEMENT_REQUIRED', 'REVIEW_REQUIRED']
                .includes(item.original_status)) {
                return {text: 'AI 증빙 분석', reason};
            }
            return null;
        },
        // ── 문항 판정 테이블 정렬/필터 ───────────────────────────────
        itemFilterOptions(label) {
            return [{value: 'ALL', text: `${label}: 전체`}, ...this.statusOptions];
        },
        // 상태 정렬 — 우선순위: 보완 필요 > 평가자 확인 > 보완 불필요 > 처리 실패 (조치 필요 순). 정렬 없으면 받은 순서 그대로
        sortItems(list) {
            const {key, dir} = this.itemSort;
            if (!key) return list;
            const rank = {
                SUPPLEMENT_REQUIRED: 0, REVIEW_REQUIRED: 1,
                NO_SUPPLEMENT: 2, PROCESSING_FAILED: 3,
            };
            const field = key === 'original' ? 'original_status' : 'current_status';
            return [...list].sort((a, b) => {
                const ra = rank[a[field]] != null ? rank[a[field]] : 99;
                const rb = rank[b[field]] != null ? rank[b[field]] : 99;
                if (ra !== rb) return (ra - rb) * dir;
                // 같은 상태 안에서는 문항 번호순 유지 (숫자 인지 정렬)
                return String(a.sub_code).localeCompare(String(b.sub_code),
                    undefined, {numeric: true});
            });
        },
        // 문항 = 문항 그룹명 + 회색 대분류. 둘이 같으면(기본정보 ST 문항) 한 번만 쓴다
        questionGroupLabel(item) {
            const name = item.question_group_name || '';
            const major = item.major_category || '';
            const shownMajor = name && major && major !== name ? major : '';
            return {
                name: name || major || '-',
                major: shownMajor,
                title: [shownMajor, name || major].filter(Boolean).join(' · ') || null,
            };
        },
        toggleItemSort(key) {
            // 클릭 사이클: 오름차순 → 내림차순 → 해제(원본 순서)
            if (this.itemSort.key !== key) {
                this.itemSort = {key, dir: 1};
            } else if (this.itemSort.dir === 1) {
                this.itemSort = {key, dir: -1};
            } else {
                this.itemSort = {key: null, dir: 1};
            }
        },
        itemSortIcon(key) {
            if (this.itemSort.key !== key) return 'arrows-sort';
            return this.itemSort.dir === 1 ? 'arrow-up' : 'arrow-down';
        },
        itemAriaSort(key) {
            if (this.itemSort.key !== key) return 'none';
            return this.itemSort.dir === 1 ? 'ascending' : 'descending';
        },
        statusVariant(status) {
            return {
                NO_SUPPLEMENT: 'success',
                SUPPLEMENT_REQUIRED: 'danger',
                REVIEW_REQUIRED: 'warning',
                PROCESSING_FAILED: 'secondary',
            }[status] || 'light';
        },
        jobStatusVariant(status) {
            return {
                DONE: 'success', PARTIAL: 'warning', FAILED: 'danger',
            }[status] || 'info';
        },
    },
};
</script>

<style scoped>
.html-split-editor {
    min-height: 500px;
}

.chat-box {
    background: #fafafa;
}

/* 패널/수신자 접기·펼치기 아이콘 버튼 — 딱 봐도 눌리는 버튼처럼 */
.sr-panel-toggle {
    border: 1px solid #adb5bd;
    background: #f8f9fa;
    padding: .15rem .4rem;
    line-height: 1;
}
.sr-panel-toggle:hover {
    background: #e9ecef;
}

/* 답변 ↔ 주장별 판정 하이라이트 */
.sr-answer-box {
    max-height: 420px;
    overflow-y: auto;
    background: #fff;
}
/* 범례 — 하이라이트 색이 무엇을 뜻하는지 칩으로 명시 */
.sr-legend {
    font-size: .78rem;
    color: #777;
}
.sr-chip {
    display: inline-block;
    padding: 1px 7px;
    margin-left: 4px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: .72rem;
}

/* 답변 옵션 노드 — 폼 컨트롤(라디오/체크박스)을 감싸 판정색을 얹는다 */
.sr-node {
    padding: 4px 8px;
    border-radius: 5px;
    border: 1px solid transparent;
    margin-bottom: 4px;
    transition: box-shadow .1s ease;
}
.sr-node-plain {
    padding: 4px 8px;
    margin-bottom: 4px;
    border: 1px solid transparent;
}
/* 선택 옵션 하위 항목 — 들여쓰기 */
.sr-elements {
    margin-left: 1.5rem;
    margin-top: 2px;
}
.sr-subnode {
    padding: 3px 6px;
    border-radius: 5px;
    border: 1px solid transparent;
    margin-bottom: 2px;
}
.sr-written {
    font-size: .9rem;
    padding-left: 1.7rem;
}
.sr-node-unsupported { background: #f8d7da; border-color: #dc3545; }
.sr-node-supported   { background: #d4edda; border-color: #28a745; }
.sr-node-exempt      { background: #e2e3e5; border-color: #ced4da; }
.sr-node-review      { background: #fff3cd; border-color: #ffc107; }
.sr-node-active {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, .5);
}
.font-weight-500 { font-weight: 500; }

.sr-claim-card {
    padding: 8px 10px;
    border: 1px solid #e6e6e6;
    border-left-width: 4px;
    border-radius: 4px;
    margin-bottom: 8px;
    background: #fff;
}
.sr-claim-unsupported { border-left-color: #dc3545; }
.sr-claim-supported   { border-left-color: #28a745; }
.sr-claim-exempt      { border-left-color: #adb5bd; }
.sr-claim-review      { border-left-color: #ffc107; }
.sr-claim-hover {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, .55);
}
.sr-claim-list {
    max-height: 420px;
    overflow-y: auto;
    /* overflow 스크롤 컨테이너는 4방향을 클리핑한다(overflow-y:auto → overflow-x:auto).
       hover box-shadow(카드 바깥 2px 링)가 좌우·상단 경계에서 잘리지 않도록 여백 확보. */
    padding: 4px;
}
.sr-att {
    padding: 3px 4px;
    font-size: .9rem;
}
.sr-att-link {
    cursor: pointer;
}
.sr-att-cited {
    background: #e7f5ff;
    border-radius: 4px;
}
/* 테이블형 답변 — 라벨(비편집) 셀 구분 */
.sr-answer-table .sr-table-label {
    background: #f8f9fa;
    font-weight: 500;
    width: 35%;
}

/* ── 문항 상세 모달: 좌 본문 / 우 접이식 AI 대화 패널 ── */
.sr-detail-layout {
    display: flex;
    align-items: stretch;
    gap: 12px;
}
.sr-detail-main {
    flex: 1 1 auto;
    min-width: 0;  /* 내부 표/긴 텍스트가 패널을 밀어내지 않도록 */
}
.sr-chat-panel {
    flex: 0 0 auto;
    display: flex;
    align-items: stretch;
    border-left: 1px solid #dee2e6;
    padding-left: 8px;
}
.sr-chat-handle {
    width: 30px;
    border: none;
    border-radius: 4px;
    background: #f8f9fa;
    color: #495057;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 0;
    gap: 6px;
    cursor: pointer;
}
.sr-chat-handle:hover {
    background: #e9ecef;
}
.sr-chat-handle-label {
    writing-mode: vertical-rl;
    font-size: 12px;
    letter-spacing: 1px;
    white-space: nowrap;
}
.sr-chat-body {
    width: 340px;
    margin-left: 8px;
    display: flex;
    flex-direction: column;
    /* 모달 본문이 길어도 대화 패널은 화면에 고정 (스크롤 컨테이너 = .modal) */
    position: sticky;
    top: 12px;
    align-self: flex-start;
    max-height: calc(100vh - 140px);
}
.sr-chat-scroll {
    flex: 1 1 auto;
    min-height: 180px;
    overflow-y: auto;
    /* 하단(최신 턴) 고정 — 신규 마운트/접힘 토글에도 브라우저가 유지한다 */
    display: flex;
    flex-direction: column-reverse;
}
@media (max-width: 1199.98px) {
    /* modal-xl 이 1140px 미만으로 줄어드는 구간 — 패널 폭 축소 */
    .sr-chat-body {
        width: 280px;
    }
}
@media (max-width: 991.98px) {
    /* modal-xl 이 500px 로 떨어지는 구간부터는 패널을 본문 아래로 스택 */
    .sr-detail-layout {
        flex-direction: column;
    }
    .sr-chat-panel {
        border-left: none;
        border-top: 1px solid #dee2e6;
        padding-left: 0;
        padding-top: 8px;
        flex-direction: column;
    }
    .sr-chat-handle {
        width: 100%;
        flex-direction: row;
        justify-content: center;
        padding: 6px 0;
    }
    .sr-chat-handle-label {
        writing-mode: horizontal-tb;
    }
    /* 스택 모드에서는 패널이 아래로 펼쳐지므로 화살표 방향을 상하로 회전 */
    .sr-chat-arrow {
        display: inline-block;
        transform: rotate(-90deg);
    }
    .sr-chat-body {
        width: 100%;
        margin-left: 0;
        margin-top: 8px;
        position: static;
        max-height: 340px;
    }
}
</style>

<style>
/* 문항 판정 표 — table 은 responsive 감싸개 안이라 scoped 가 닿지 않는다.
   고정 열 합 656(번호 96 · 근거 128 · AI 판정 168 · 현재 상태 128 · 메일 72 · 확인 64) + 질문 최소 ~244
   (전체 목록 보기는 문항 · 질문 ~192씩). 1366(메뉴 펼침) 본문 폭 약 1076 안에 들어가게 — 그보다 좁으면 가로 스크롤 */
.table.sr-item-table {
    min-width: 900px;
}
.table.sr-item-table.sr-item-table-list {
    min-width: 1040px;
}
/* 묶음 머리 줄 — 문항 행(44)보다 낮은 36 · 머리와 같은 sunken 면 · 굵은 글자. 누를 수 없어 hover 면을 끈다.
   공통 DS 표 규칙(.admin-shell-main .table…)보다 구체적이어야 해서 ds-table 까지 붙인다 */
.admin-shell-main .table.ds-table.sr-item-table > tbody > tr.sr-group-row > th {
    height: 36px;
    padding: 0 16px;
    vertical-align: middle;
    border-top: 1px solid var(--ds-border);
    border-bottom: 1px solid var(--ds-border);
    background-color: var(--ds-surface-sunken);
    color: var(--ds-fg);
    font-weight: 600;
}
.admin-shell-main .table.ds-table.sr-item-table > tbody > tr.sr-group-row:hover {
    background-color: transparent;
}
.sr-item-table .sr-group-row .ds-sub {
    font-weight: 400;
}
/* 묶음 끝 행 아래 선은 다음 머리 줄 위 선이 대신한다(공통 규칙이 tbody 마지막 행 선을 지운다) */
.admin-shell-main .table.ds-table.sr-item-table > tbody:first-of-type > tr.sr-group-row > th {
    border-top: 0;
}

/* 문항 상세 모달 폭 확장 — b-modal dialog-class 대상이라 전역 스타일 사용.
   패널을 열면 모달 폭이 패널 증가분만큼 늘어나 좌측 본문 폭은 닫힘 상태와
   동일하게 유지된다 (본문 레이아웃이 뭉개지지 않음) */
.modal-dialog.sr-detail-dialog {
    transition: max-width .15s ease-out;
}
@media (min-width: 1200px) {
    /* modal-xl 1140px + 펼침 증가분(패널 본문 340px + 마진 8px) */
    .modal-dialog.sr-detail-dialog.sr-chat-expanded {
        max-width: min(1488px, calc(100vw - 3rem));
    }
}
@media (min-width: 992px) and (max-width: 1199.98px) {
    /* 이 구간 modal-xl 800px + 증가분(280px + 8px) */
    .modal-dialog.sr-detail-dialog.sr-chat-expanded {
        max-width: min(1088px, calc(100vw - 2rem));
    }
}
</style>
